'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { elementProps } from '@/lib/elementHelpers'
import { imageFocal } from '@/lib/contenuEditable'
import type { FeaturesConfig, FeaturesContent, FeatureItem } from '@/types/sections'
import type { SectionConfig } from '@/types/site'
import { Check, ArrowRight, Star } from 'lucide-react'
import { DynamicIcon } from '@/components/ui/DynamicIcon'
import { getTitleSizeClass, getTextAlignClass } from '../_utils'
import { EditablePlaceholder } from '../_EditablePlaceholder'
import { DecorativeOrnament, FloatingIllustration } from '../_DecorativeOrnament'

interface FeaturesSectionProps {
  config: SectionConfig
  isEditing?: boolean
}

export function FeaturesSection({ config, isEditing }: FeaturesSectionProps) {
  const features = config as FeaturesConfig
  const content = (features.content ?? {}) as Partial<FeaturesContent>
  const { accentColor, textColor: customTextColor, titleSize, textAlign } = config.style
  const items: FeatureItem[] = content.items ?? []
  const variant = features.variant ?? 'startup-grid'
  const universe = variant.split('-')[0]

  const title = content.title
  const subtitle = content.subtitle
  const eyebrow = content.eyebrow

  // ═══════════════════════════════════════════
  // STARTUP — SaaS moderne
  // ═══════════════════════════════════════════

  if (universe === 'startup') {
    const accent = accentColor ?? '#6366f1'
    const layout = variant.replace('startup-', '')

    const header = (
      <div className={cn("text-center mb-14 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full border border-zinc-200 text-zinc-600 bg-white shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-bold text-zinc-900 leading-tight", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor } : undefined}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{subtitle}</p>}
      </div>
    )

    // Grid
    if (layout === 'grid') {
      return (
        <section className="bg-zinc-50 py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item, i) => (
                <div key={item.id} className="bg-white rounded-2xl p-7 border border-zinc-100 shadow-sm hover:shadow-md transition-shadow group">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-xl mb-5 transition-colors"
                    style={{ backgroundColor: `${accent}12` }}
                  >
                    <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-zinc-900 mb-2">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // Bento
    if (layout === 'bento') {
      return (
        <section className="bg-white py-20" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className={cn(
                    'rounded-2xl p-7 border border-zinc-100 bg-zinc-50 hover:bg-white transition-colors',
                    i === 0 && 'lg:col-span-2 lg:row-span-2 lg:p-10',
                  )}
                >
                  <div
                    className={cn('rounded-xl flex items-center justify-center text-xl mb-5', i === 0 ? 'w-14 h-14' : 'w-11 h-11')}
                    style={{ backgroundColor: `${accent}12` }}
                  >
                    <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className={cn('font-semibold text-zinc-900 mb-2', i === 0 && 'text-xl')}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className={cn('text-sm text-zinc-500 leading-relaxed', i === 0 && 'text-base max-w-md')}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section className="bg-white py-20" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-6xl mx-auto px-6">
          {header}
          <div className="space-y-6 max-w-3xl mx-auto">
            {items.map((item, i) => (
              <div key={item.id} className={cn('flex items-start gap-6', i % 2 === 1 && 'flex-row-reverse text-right')}>
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl shrink-0"
                  style={{ backgroundColor: `${accent}12` }}
                >
                  <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                </div>
                <div className="flex-1 pt-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-zinc-900 mb-1">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-500 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // CORPORATE — Entreprise / Finance
  // ═══════════════════════════════════════════

  if (universe === 'corporate') {
    const accent = accentColor ?? '#3b82f6'
    const layout = variant.replace('corporate-', '')

    const header = (
      <div className={cn("mb-14 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-block text-xs font-semibold px-3 py-1 rounded border text-blue-300 border-blue-500/30 bg-blue-500/10">
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-bold text-white leading-tight", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor } : undefined}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-slate-400 max-w-2xl">{subtitle}</p>}
      </div>
    )

    // Grid
    if (layout === 'grid') {
      return (
        <section className="bg-brand-ink py-20 relative" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="relative max-w-7xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item, i) => (
                <div key={item.id} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 hover:border-blue-500/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg mb-4 bg-blue-500/10 border border-blue-500/20">
                    <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-white mb-2">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // Bento
    if (layout === 'bento') {
      return (
        <section className="bg-brand-ink py-20 relative" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="relative max-w-7xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className={cn(
                    'rounded-xl p-6 border-l-2 bg-slate-800/30',
                    i === 0 ? 'lg:col-span-2 lg:p-8 border-blue-400' : 'border-slate-600',
                  )}
                  style={i === 0 ? { borderLeftColor: accent } : undefined}
                >
                  <div className="mb-3"><DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-lg leading-none" /></div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className={cn('font-semibold text-white mb-2', i === 0 && 'text-xl')}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section className="bg-brand-ink py-20 relative" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="relative max-w-7xl mx-auto px-6">
          {header}
          <div className="max-w-3xl space-y-0 divide-y divide-slate-700/50">
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-5 py-6 first:pt-0 last:pb-0">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0 bg-slate-800 border border-slate-700">
                  <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                </div>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-white mb-1">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 shrink-0 mt-1" />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // LUXE — Hôtellerie / Mode / Joaillerie
  // ═══════════════════════════════════════════

  if (universe === 'luxe') {
    const gold = accentColor ?? '#CBB68B'
    const layout = variant.replace('luxe-', '')
    const hasDecorativeIcon = !!content.decorativeIcon

    const header = (
      <div className={cn("text-center mb-16 space-y-5", textAlign && getTextAlignClass(textAlign))}>
        {hasDecorativeIcon && <DecorativeOrnament color={gold} iconUrl={typeof content.decorativeIcon === 'string' && content.decorativeIcon.startsWith('http') ? content.decorativeIcon : undefined} />}
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-block text-xs tracking-[0.25em] uppercase font-normal" style={{ color: gold }}>
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-black text-zinc-900 leading-tight tracking-tight", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor } : undefined}>
            {title}
          </h2>
        )}
        {!hasDecorativeIcon && <div className="w-12 h-px mx-auto" style={{ background: gold }} />}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base text-zinc-400 max-w-xl mx-auto tracking-wide font-normal">{subtitle}</p>}
      </div>
    )

    // Grid
    if (layout === 'grid') {
      const cols = content.columns ?? 3
      const colClass = cols === 2 ? 'sm:grid-cols-2' : cols === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'
      const useHorizontal = cols === 2

      return (
        <section className="bg-white py-24 relative overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6 relative">
            {header}
            <div className={cn('grid gap-10', colClass)}>
              {items.map((item, i) => useHorizontal ? (
                <div key={item.id} className="flex gap-5 items-start">
                  {item.icon?.startsWith('http') ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={item.icon} alt="" className="w-10 h-10 shrink-0 object-contain" style={{ filter: 'none' }} />
                  ) : (
                    <DynamicIcon name={item.icon} className="w-10 h-10 shrink-0" fallbackClassName="text-3xl leading-none" style={{ color: gold }} />
                  )}
                  <div className="space-y-2">
                    <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-bold text-zinc-900 tracking-wide text-sm uppercase">{item.title}</h3>
                    <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-400 leading-relaxed font-normal">{item.description}</p>
                  </div>
                </div>
              ) : (
                <div key={item.id} className="text-center space-y-4">
                  {item.icon?.startsWith('http') ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={item.icon} alt="" className="w-7 h-7 mx-auto object-contain" style={{ filter: 'none' }} />
                  ) : (
                    <DynamicIcon name={item.icon} className="w-7 h-7 mx-auto" fallbackClassName="text-2xl leading-none" style={{ color: gold }} />
                  )}
                  <div className="w-8 h-px mx-auto" style={{ background: gold }} />
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-medium text-zinc-900 tracking-wide text-sm uppercase">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-400 leading-relaxed font-normal">{item.description}</p>
                </div>
              ))}
            </div>
            {(content.primaryButton || content.secondaryButton) && (
              <div className="flex flex-wrap gap-4 justify-center mt-12">
                {content.primaryButton && (
                  <a {...elementProps(config.id, 'primaryButton', 'button')} href={content.primaryButton.href} className="px-8 py-3.5 text-sm font-medium tracking-[0.1em] uppercase text-white transition-all hover:brightness-110" style={{ backgroundColor: gold }}>
                    {content.primaryButton.label}
                  </a>
                )}
                {content.secondaryButton && (
                  <a {...elementProps(config.id, 'secondaryButton', 'button')} href={content.secondaryButton.href} className="px-8 py-3.5 text-sm font-medium tracking-[0.1em] uppercase border border-zinc-300 text-zinc-700 hover:bg-zinc-50 transition-colors">
                    {content.secondaryButton.label}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      )
    }

    // Bento
    if (layout === 'bento') {
      return (
        <section className="bg-zinc-50 py-24 relative overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6 relative">
            {header}
            <div className="grid sm:grid-cols-2 gap-6">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className={cn(
                    'bg-white p-8 border border-zinc-100',
                    i === 0 && 'sm:col-span-2 sm:p-12',
                  )}
                >
                  <span className="text-xs font-normal tracking-[0.2em] uppercase" style={{ color: gold }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className={cn('font-medium text-zinc-900 mt-3 mb-3 tracking-wide', i === 0 ? 'text-xl' : 'text-sm uppercase')}>
                    {item.title}
                  </h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-400 leading-relaxed font-normal">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section className="bg-white py-24 relative overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6 relative">
          {header}
          <div className="space-y-10">
            {items.map((item, i) => (
              <div key={item.id} className="flex gap-8 items-start">
                <span className="text-2xl font-normal tracking-wide shrink-0 w-10 pt-1" style={{ color: gold }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 border-t border-zinc-100 pt-4">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-medium text-zinc-900 mb-2 tracking-wide text-sm uppercase">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-400 leading-relaxed font-normal">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // CREATIVE — Agence / Portfolio / Studio
  // ═══════════════════════════════════════════

  if (universe === 'creative') {
    const accent = accentColor ?? '#ea580c'
    const layout = variant.replace('creative-', '')

    const header = (
      <div className={cn("mb-14 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow ? (
          <span
            {...elementProps(config.id, 'eyebrow', 'badge')}
            className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1.5 border-2 border-zinc-900 text-zinc-900"
            style={accentColor ? { backgroundColor: accentColor, borderColor: accentColor, color: '#fff' } : undefined}
          >
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-5xl font-black text-zinc-900 leading-[0.95]", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor } : undefined}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base text-zinc-600 max-w-lg">{subtitle}</p>}
      </div>
    )

    // Grid
    if (layout === 'grid') {
      return (
        <section className="bg-[#f5f0e8] py-20" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-7xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item, i) => (
                <div key={item.id} className="relative bg-white border-2 border-zinc-900 p-6 hover:translate-x-0.5 hover:-translate-y-0.5 transition-transform shadow-[4px_4px_0_0_#18181b] hover:shadow-[6px_6px_0_0_#18181b]">
                  <div className="mb-4"><DynamicIcon name={item.icon} className="w-7 h-7" fallbackClassName="text-2xl leading-none" /></div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-bold text-zinc-900 mb-2 uppercase text-sm tracking-wide">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // Bento
    if (layout === 'bento') {
      return (
        <section className="bg-[#f5f0e8] py-20" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-7xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className={cn(
                    'bg-white border-2 border-zinc-900 p-6 relative',
                    i === 0 && 'lg:col-span-2 lg:row-span-2 lg:p-10',
                  )}
                >
                  <span className="absolute top-3 right-3 text-[10px] font-black uppercase tracking-wider text-zinc-300">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="mb-4"><DynamicIcon name={item.icon} className={cn('w-7 h-7', i === 0 && 'w-10 h-10')} fallbackClassName={cn('text-2xl leading-none', i === 0 && 'text-4xl')} /></div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className={cn('font-black text-zinc-900 uppercase tracking-wide', i === 0 ? 'text-2xl mb-3' : 'text-sm mb-2')}>
                    {item.title}
                  </h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section className="bg-[#f5f0e8] py-20" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-4xl mx-auto px-6">
          {header}
          <div className="space-y-0 border-t-2 border-zinc-900">
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-6 py-6 border-b-2 border-zinc-900">
                <span className="text-4xl md:text-5xl font-black text-zinc-200 leading-none shrink-0 w-16" style={accentColor ? { color: accentColor } : undefined}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-black text-zinc-900 uppercase tracking-wide text-sm mb-1">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-600 leading-relaxed">{item.description}</p>
                </div>
                <div className="shrink-0 mt-1"><DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" /></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // ECOMMERCE — Boutique / Marketplace
  // ═══════════════════════════════════════════

  if (universe === 'ecommerce') {
    const accent = accentColor ?? '#059669'
    const layout = variant.replace('ecommerce-', '')

    const header = (
      <div className={cn("text-center mb-14 space-y-3", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-block text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: accent }}>
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-bold text-zinc-900 leading-tight", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor } : undefined}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base text-zinc-500 max-w-xl mx-auto">{subtitle}</p>}
      </div>
    )

    // Grid
    if (layout === 'grid') {
      return (
        <section className="bg-white py-16" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item, i) => (
                <div key={item.id} className="rounded-2xl p-6 border border-zinc-100 bg-zinc-50 hover:border-emerald-200 transition-colors">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg" style={{ backgroundColor: `${accent}12` }}>
                      <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                    </div>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className="w-3 h-3 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-zinc-900 mb-2">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // Bento
    if (layout === 'bento') {
      return (
        <section className="bg-zinc-50 py-16" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className={cn(
                    'rounded-2xl p-6 bg-white border border-zinc-100',
                    i === 0 && 'lg:col-span-2 lg:p-8',
                  )}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: accent }}>
                      INCLUS
                    </span>
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className={cn('font-semibold text-zinc-900 mb-2', i === 0 && 'text-xl')}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List (Checklist)
    return (
      <section className="bg-white py-16" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-4">
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-4 p-4 rounded-xl bg-emerald-50/50 border border-emerald-100">
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: accent }}>
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-zinc-900 mb-0.5">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-500 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // GLASS — IA / Crypto / Tech
  // ═══════════════════════════════════════════

  if (universe === 'glass') {
    const accent = accentColor ?? '#818cf8'
    const layout = variant.replace('glass-', '')

    const header = (
      <div className={cn("text-center mb-14 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-flex items-center gap-2 text-xs font-medium px-4 py-1.5 rounded-full border border-white/10 text-white/60 bg-white/[0.04] backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent, boxShadow: `0 0 8px ${accent}80` }} />
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-bold gradient-text leading-tight", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor, background: 'none', WebkitTextFillColor: 'unset' } : undefined}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-white/40 max-w-2xl mx-auto">{subtitle}</p>}
      </div>
    )

    // Grid
    if (layout === 'grid') {
      return (
        <section className="bg-zinc-950 py-20 noise-overlay relative overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="absolute inset-0 dot-grid" />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full opacity-30 blur-3xl pointer-events-none"
            style={{ background: `radial-gradient(ellipse, ${accent}20, transparent 70%)` }}
          />
          <div className="relative max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => (
                <div key={item.id} className="bg-white/[0.04] backdrop-blur-xl rounded-2xl p-6 border border-white/[0.08] hover:border-white/[0.15] transition-colors group">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-lg mb-4 border border-white/10"
                    style={{ backgroundColor: `${accent}10` }}
                  >
                    <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" />
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-white mb-2">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-white/40 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // Bento
    if (layout === 'bento') {
      return (
        <section className="bg-zinc-950 py-20 noise-overlay relative overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="absolute inset-0 dot-grid" />
          <div className="relative max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className={cn(
                    'bg-white/[0.03] backdrop-blur-xl rounded-2xl p-6 border border-white/[0.06] relative overflow-hidden',
                    i === 0 && 'lg:col-span-2 lg:row-span-2 lg:p-10',
                  )}
                >
                  {/* Glow accent line at top */}
                  <div className="absolute top-0 left-[20%] right-[20%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}40, transparent)` }} />
                  <div className="mb-4"><DynamicIcon name={item.icon} className={cn('w-5 h-5', i === 0 && 'w-8 h-8')} fallbackClassName={cn('text-xl leading-none', i === 0 && 'text-3xl')} /></div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className={cn('font-semibold text-white mb-2', i === 0 && 'text-xl')}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className={cn('text-sm text-white/40 leading-relaxed', i === 0 && 'text-base text-white/50 max-w-md')}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section className="bg-zinc-950 py-20 noise-overlay relative overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="absolute inset-0 dot-grid" />
        <div className="relative max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-3">
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-5 p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] backdrop-blur-sm hover:bg-white/[0.06] transition-colors">
                <div className="mt-0.5"><DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" /></div>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold text-white mb-1">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-white/40 leading-relaxed">{item.description}</p>
                </div>
                <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ backgroundColor: accent, boxShadow: `0 0 6px ${accent}60` }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // CANOPY — Brand Pillars / Eco Values
  // ═══════════════════════════════════════════

  if (variant === 'canopy-values') {
    const defaultItems: FeatureItem[] = [
      { id: 'canopy-1', icon: '🌿', title: 'Sustainable Materials', description: 'Every material is chosen with intention — renewable, responsibly sourced, and designed to leave the lightest footprint possible.' },
      { id: 'canopy-2', icon: '🌎', title: 'Carbon Neutral', description: 'We offset 100% of our carbon emissions across our entire supply chain, from raw materials to your doorstep.' },
      { id: 'canopy-3', icon: '♻️', title: 'Circular Design', description: 'Products built to last, and when they reach end of life, every component can be recycled or composted.' },
    ]

    const resolvedItems = items.length > 0 ? items : defaultItems

    const cardRevealRef = (delay: number) => (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(32px)'
      el.style.transition = `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Values Section')}
        style={{
          position: 'relative',
          fontFamily: 'var(--font-body, inherit)',
          overflow: 'hidden',
        }}
      >
        {/* Split background */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
          }}
        >
          <div style={{ height: '50%', backgroundColor: '#FFFFFF' }} />
          <div style={{ height: '50%', backgroundColor: '#F0EDE8' }} />
        </div>

        <style>{`
          .canopy-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          .canopy-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 32px rgba(0,0,0,0.1) !important;
          }
          .canopy-card:hover .canopy-icon-wrap {
            transform: scale(1.05);
          }
          .canopy-icon-wrap {
            transition: transform 0.3s ease;
          }
          @media (max-width: 767px) {
            .canopy-grid {
              grid-template-columns: 1fr !important;
              max-width: 400px !important;
              margin-left: auto !important;
              margin-right: auto !important;
            }
          }
        `}</style>

        <div
          {...elementProps(config.id, 'inner', 'container', 'Inner Container')}
          style={{
            position: 'relative',
            zIndex: 1,
            maxWidth: 1100,
            margin: '0 auto',
            padding: 'clamp(80px, 12vw, 160px) 24px',
          }}
        >
          {/* Header */}
          <div
            {...elementProps(config.id, 'headerArea', 'container', 'Header Area')}
            style={{
              textAlign: 'center',
              marginBottom: 64,
            }}
          >
            {(title || eyebrow) && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  fontSize: 'clamp(32px, 4vw, 40px)',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  margin: '0 0 16px 0',
                  ...(customTextColor ? { color: customTextColor } : { color: '#1A1A1A' }),
                }}
              >
                {title || eyebrow}
              </h2>
            )}
            {subtitle && (
              <p
                {...elementProps(config.id, 'subtitle', 'text')}
                style={{
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: '#666666',
                  maxWidth: 600,
                  margin: '0 auto',
                }}
              >
                {subtitle}
              </p>
            )}
          </div>

          {/* Cards Grid */}
          <div
            {...elementProps(config.id, 'grid', 'container', 'Cards Grid')}
            className="canopy-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 40,
            }}
          >
            {resolvedItems.map((item, i) => (
              <div
                key={item.id}
                ref={cardRevealRef(i * 0.15)}
                {...elementProps(config.id, `items.${i}`, 'container', 'Value Card')}
                className="canopy-card"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: 0,
                  boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
                  padding: 40,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                {/* Icon circle */}
                <div
                  {...elementProps(config.id, `items.${i}.iconWrap`, 'container', 'Icon Container')}
                  className="canopy-icon-wrap"
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: '#F0EDE8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    lineHeight: 1,
                  }}
                >
                  {item.icon || defaultItems[i % defaultItems.length]?.icon || '✦'}
                </div>

                {/* Title */}
                <h3
                  {...elementProps(config.id, `items.${i}.title`, 'heading')}
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#1A1A1A',
                    marginTop: 24,
                    marginBottom: 12,
                    lineHeight: 1.3,
                  }}
                >
                  {item.title || `Value ${i + 1}`}
                </h3>

                {/* Description */}
                <p
                  {...elementProps(config.id, `items.${i}.description`, 'text')}
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: '#666666',
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {item.description || 'Description of this core value and what it means for our commitment.'}
                </p>

                {/* Bottom accent line */}
                <div
                  {...elementProps(config.id, `items.${i}.accent`, 'container', 'Accent Line')}
                  style={{
                    width: 48,
                    height: 2,
                    backgroundColor: accentColor ?? '#2D5016',
                    marginTop: 24,
                    borderRadius: 1,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // OBSCURA — Photographer Services Accordion
  // ═══════════════════════════════════════════

  if (variant === 'obscura-accordion') {
    const hideTitleOverlay = Boolean((config.content as Record<string, unknown> | undefined)?.hideTitleOverlay)
    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelLabels = [
      { title: 'Portrait', description: 'Séances photo individuelles et professionnelles' },
      { title: 'Mariage', description: 'Reportage complet de votre plus beau jour' },
      { title: 'Événement', description: 'Couverture corporate et événementielle' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelLabels[i % defaultPanelLabels.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelLabels[i % defaultPanelLabels.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelLabels.map((label, i) => ({ ...label, image: panelImages[i], id: `default-${i}` }))

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Services Section')} className="overflow-hidden" style={{ backgroundColor: '#0A0A0A', fontFamily: 'var(--font-body, inherit)' }}>
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} className="px-4 md:px-[60px]">
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <div>
                <h2
                  {...elementProps(config.id, 'title', 'heading')}
                  className={cn('italic', titleSize && getTitleSizeClass(titleSize))}
                  style={{
                    maxWidth: 700,
                    margin: '0 auto',
                    fontFamily: 'var(--font-heading, inherit)',
                    fontSize: 'clamp(1.5rem, 0.95rem + 2.4vw, 3.5rem)',
                    fontWeight: 400,
                    lineHeight: '125%',
                    marginBottom: 60,
                    color: customTextColor ?? '#E8E4DF',
                  }}
                >
                  {title}
                </h2>
              </div>
            )}
          </div>
        </div>

        {/* 3 equal panels — image zoom on hover */}
        <style>{`
          .obscura-panel:hover .obscura-panel-img { transform: scale(1.08) !important; }
          .obscura-panel:hover .obscura-panel-overlay { background-color: rgba(10,10,10,0.2) !important; }
          .obscura-panel:hover .obscura-panel-arrow { opacity: 1 !important; top: calc(100% + 16px) !important; }
          .obscura-panel:hover .obscura-panel-desc { opacity: 0.7 !important; transform: translateY(0) !important; }
          @media (max-width: 767px) {
            .obscura-panels-row { flex-direction: column !important; }
            .obscura-panel { min-height: 360px !important; }
            /* Mobile: description always visible (no hover available on touch) */
            .obscura-panel .obscura-panel-desc { opacity: 0.8 !important; transform: translateY(0) !important; }
            .obscura-panel .obscura-panel-overlay { background-color: rgba(10,10,10,0.55) !important; }
          }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="obscura-panels-row flex flex-row"
        >
          {resolvedPanels.map((panel, i) => (
            <div
              key={panel.id}
              {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
              className="obscura-panel relative overflow-hidden cursor-pointer"
              style={{
                flex: '1 1 0%',
                minHeight: 'clamp(360px, 60vw, 700px)',
                backgroundColor: '#0A0A0A',
                color: '#E8E4DF',
              }}
            >
              {/* Background image */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                src={panel.image}
                alt={panel.title}
                className="obscura-panel-img absolute inset-0 w-full h-full object-cover"
                style={{ transition: 'transform 0.7s ease' }}
              />

              {/* Overlay */}
              <div
                {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                className="obscura-panel-overlay absolute inset-0"
                style={{
                  backgroundColor: 'rgba(10,10,10,0.5)',
                  zIndex: 1,
                  transition: 'background-color 0.4s',
                }}
              />

              {/* Content */}
              {!hideTitleOverlay && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ zIndex: 2 }}
              >
                <div className="relative flex flex-col items-center" style={{ gap: 40, textAlign: 'center', padding: '0 24px' }}>
                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    className="italic"
                    style={{
                      fontFamily: 'var(--font-heading, inherit)',
                      fontSize: 'clamp(1.5rem, 1.0714rem + 1.9048vw, 2.5rem)',
                      fontWeight: 400,
                      color: '#E8E4DF',
                      textAlign: 'center',
                      margin: 0,
                    }}
                  >
                    {panel.title}
                  </h4>

                  {/* Description — slides up on hover */}
                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    className="obscura-panel-desc"
                    style={{
                      fontFamily: '"Inter Variable", sans-serif',
                      fontSize: 'clamp(13px, 0.8125rem + 0.2vw, 15px)',
                      fontWeight: 400,
                      lineHeight: '160%',
                      color: '#8A8480',
                      margin: 0,
                      maxWidth: 280,
                      opacity: 0,
                      transform: 'translateY(12px)',
                      transition: 'opacity 0.5s ease, transform 0.5s ease',
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Arrow icon */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    className="obscura-panel-arrow"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 40px)',
                      opacity: 0,
                      transition: 'top 0.5s ease, opacity 0.5s ease',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(212, 168, 83, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#CBB68B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
              )}
            </div>
          ))}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // NACRE — Horizontal Accordion (Nail Salon)
  // ═══════════════════════════════════════════

  if (variant === 'nacre-accordion') {
    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Manucure', description: 'Pose de vernis, french, nail art personnalis\u00e9' },
      { title: 'P\u00e9dicure', description: 'Soin complet des pieds, vernis semi-permanent' },
      { title: 'Extensions', description: 'Pose gel, r\u00e9sine, capsules sur-mesure' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Services Section')} className="overflow-hidden" style={{ backgroundColor: '#2A1A1E', fontFamily: 'var(--font-body, inherit)' }}>
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <div>
                <h2
                  {...elementProps(config.id, 'title', 'heading')}
                  className={cn(titleSize && getTitleSizeClass(titleSize))}
                  style={{
                    maxWidth: 700,
                    margin: '0 auto',
                    fontFamily: 'var(--font-sans), sans-serif',
                    fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                    fontWeight: 500,
                    lineHeight: '110%',
                    textTransform: 'capitalize' as const,
                    marginBottom: 60,
                    color: '#F0E0DA',
                  }}
                >
                  {title}
                </h2>
              </div>
            )}
          </div>
        </div>

        {/* 3 equal panels — only image zooms on hover, panels stay same size */}
        <style>{`
          .nacre-panel:hover .nacre-panel-img { transform: scale(1.08) !important; }
          .nacre-panel:hover .nacre-panel-overlay { background-color: rgba(0,0,0,0.2) !important; }
          .nacre-panel:hover .nacre-panel-arrow { opacity: 1 !important; top: calc(100% + 16px) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => (
            <div
              key={panel.id}
              {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
              className="nacre-panel relative overflow-hidden cursor-pointer"
              style={{
                flex: '1 1 0%',
                minHeight: 700,
                backgroundColor: '#2A1A1E',
                color: '#F0E0DA',
              }}
            >
              {/* Background image */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                src={panel.image}
                alt={panel.title}
                className="nacre-panel-img absolute inset-0 w-full h-full object-cover"
                style={{ transition: 'transform 0.7s ease' }}
              />

              {/* Overlay */}
              <div
                {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                className="nacre-panel-overlay absolute inset-0"
                style={{
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  zIndex: 1,
                  transition: 'background-color 0.4s',
                }}
              />

              {/* Content */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ zIndex: 2 }}
              >
                <div className="relative flex flex-col items-center" style={{ gap: 40 }}>
                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: 'var(--font-sans), sans-serif',
                      fontSize: 'clamp(1.5rem, 1.0714rem + 1.9048vw, 2.5rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#ffffff',
                      textAlign: 'center',
                      margin: 0,
                    }}
                  >
                    {panel.title}
                  </h4>

                  {/* Description */}
                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "'Inter Variable', sans-serif",
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'rgba(240, 224, 218, 0.7)',
                      textAlign: 'center',
                      margin: 0,
                      maxWidth: 220,
                      lineHeight: '150%',
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Arrow icon */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    className="nacre-panel-arrow"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 40px)',
                      opacity: 0,
                      transition: 'top 0.5s ease, opacity 0.5s ease',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(201, 169, 110, 0.3)',
                      backdropFilter: 'blur(20px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // BRAISE — Restaurant Horizontal Accordion
  // ═══════════════════════════════════════════

  if (variant === 'braise-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Déjeuner', description: 'Une cuisine raffinée pour sublimer votre pause méridienne' },
      { title: 'Dîner', description: 'Une expérience gastronomique inoubliable en soirée' },
      { title: 'Événements', description: 'Privatisez notre espace pour vos réceptions d\'exception' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#1A1209', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#F5F0E8',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .braise-panel-collapsed:hover { background-color: rgba(26, 18, 9, 0.85) !important; }
          .braise-panel-expanded:hover .braise-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'braise-panel-expanded' : 'braise-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#1A1209',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="braise-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(26, 18, 9, 0.85) 0%, rgba(26, 18, 9, 0.3) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Gold number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#C8A96E',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.6,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#F5F0E8',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#F5F0E8',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#E8E4DF',
                      opacity: 0.8,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Gold glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(200, 169, 110, 0.25)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(200, 169, 110, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(200, 169, 110, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(200, 169, 110, 0.25)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#C8A96E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // FORGE — Sports Coach Horizontal Accordion
  // ═══════════════════════════════════════════

  if (variant === 'forge-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Coaching Privé', description: 'Un accompagnement sur-mesure pour atteindre vos objectifs personnels' },
      { title: 'Cours Collectifs', description: 'L\'énergie du groupe pour repousser vos limites ensemble' },
      { title: 'Nutrition', description: 'Des plans alimentaires adaptés à votre mode de vie et vos performances' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#0A0A0A', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#E8E8E8',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .forge-panel-collapsed:hover { background-color: rgba(26, 26, 26, 0.85) !important; }
          .forge-panel-expanded:hover .forge-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'forge-panel-expanded' : 'forge-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#0A0A0A',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="forge-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(10, 10, 10, 0.85) 0%, rgba(10, 10, 10, 0.3) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Orange number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#FF4D00',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.6,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#E8E8E8',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#E8E8E8',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#E8E8E8',
                      opacity: 0.8,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Orange glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 77, 0, 0.3)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 77, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 77, 0, 0.5)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 77, 0, 0.3)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#FF4D00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // CISEAUX — Hair Salon Horizontal Accordion
  // ═══════════════════════════════════════════

  if (variant === 'ciseaux-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Coupes', description: 'Des coupes sur-mesure qui subliment votre personnalité' },
      { title: 'Coloration', description: 'Des techniques de coloration expertes pour un résultat naturel et lumineux' },
      { title: 'Barbe & Soins', description: 'Un rituel de soins complet pour une barbe impeccable' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#0B0B0B', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#FFFFFF',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .ciseaux-panel-collapsed:hover { background-color: rgba(11, 11, 11, 0.85) !important; }
          .ciseaux-panel-expanded:hover .ciseaux-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'ciseaux-panel-expanded' : 'ciseaux-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#0B0B0B',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="ciseaux-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(11, 11, 11, 0.85) 0%, rgba(11, 11, 11, 0.3) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Copper number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#B76E79',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.6,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#B5B0A8',
                      opacity: 0.9,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Copper glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(183, 110, 121, 0.3)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(183, 110, 121, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(183, 110, 121, 0.5)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(183, 110, 121, 0.3)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#B76E79" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // ARCHITECTE ATELIER — Interior Architect Horizontal Accordion
  // ═══════════════════════════════════════════

  if (variant === 'atelier-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Conception', description: 'De l\'esquisse au plan 3D, nous concevons des espaces qui reflètent votre personnalité et votre mode de vie.' },
      { title: 'Réalisation', description: 'Coordination complète des artisans et suivi rigoureux pour une exécution impeccable de chaque détail.' },
      { title: 'Suivi de chantier', description: 'Présence régulière sur site, contrôle qualité et respect des délais pour une livraison sans surprise.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#1A1A1A', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#C4B5A0',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .atelier-panel-collapsed:hover { background-color: rgba(26, 26, 26, 0.85) !important; }
          .atelier-panel-expanded:hover .atelier-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'atelier-panel-expanded' : 'atelier-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#1A1A1A',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="atelier-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0.35) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Sand number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#C4B5A0',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.6,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#D6CFCA',
                      opacity: 0.9,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Bronze glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(139, 115, 85, 0.3)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(139, 115, 85, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(139, 115, 85, 0.5)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(139, 115, 85, 0.3)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // ENCRE — Horizontal Accordion (Tattoo Parlor)
  // ═══════════════════════════════════════════

  if (variant === 'encre-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Tatouage Custom', description: 'Du concept à la réalisation, chaque pièce est dessinée sur mesure pour s\'adapter parfaitement à votre morphologie et votre histoire.' },
      { title: 'Cover-up', description: 'Transformez un ancien tatouage en une œuvre d\'art nouvelle. Notre expertise en recouvrement garantit un résultat spectaculaire.' },
      { title: 'Flash Tattoo', description: 'Découvrez notre collection de designs originaux prêts à être tatoués. Des pièces uniques à prix fixe, disponibles immédiatement.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#0A0A0A', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#C41E3A',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .encre-panel-collapsed:hover { background-color: rgba(10, 10, 10, 0.85) !important; }
          .encre-panel-expanded:hover .encre-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'encre-panel-expanded' : 'encre-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#0A0A0A',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="encre-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(10, 10, 10, 0.92) 0%, rgba(10, 10, 10, 0.4) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Glassmorphism overlay strip (collapsed) */}
                {!isExpanded && (
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(10, 10, 10, 0.6)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Crimson number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#C41E3A',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.7,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  {/* Crimson number — expanded */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      color: '#C41E3A',
                      marginBottom: 12,
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    0{i + 1}
                  </span>

                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#8C8C8C',
                      opacity: 0.95,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Crimson glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(196, 30, 58, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(196, 30, 58, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(196, 30, 58, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(196, 30, 58, 0.2)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#C41E3A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // BRIXSA — Split Layout Accordion
  // ═══════════════════════════════════════════

  if (variant === 'brixsa-accordion') {
    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelLabels = ['Start Buying', 'Start Selling', 'Start Renting']

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelLabels[i % defaultPanelLabels.length],
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelLabels.map((label, i) => ({ title: label, image: panelImages[i], id: `default-${i}` }))

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Services Section')} className="overflow-hidden" style={{ backgroundColor: 'var(--color-muted, #f6efe5)', fontFamily: 'var(--font-body, inherit)' }}>
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <div>
                <h2
                  {...elementProps(config.id, 'title', 'heading')}
                  className={cn(titleSize && getTitleSizeClass(titleSize))}
                  style={{
                    maxWidth: 700,
                    margin: '0 auto',
                    fontFamily: 'var(--font-sans), sans-serif',
                    fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                    fontWeight: 500,
                    lineHeight: '110%',
                    textTransform: 'capitalize' as const,
                    marginBottom: 60,
                    ...(customTextColor ? { color: customTextColor } : { color: 'var(--color-foreground, #140c08)' }),
                  }}
                >
                  {title}
                </h2>
              </div>
            )}
          </div>
        </div>

        {/* 3 equal panels — only image zooms on hover, panels stay same size */}
        <style>{`
          .brixsa-panel:hover .brixsa-panel-img { transform: scale(1.08) !important; }
          .brixsa-panel:hover .brixsa-panel-overlay { background-color: rgba(0,0,0,0.2) !important; }
          .brixsa-panel:hover .brixsa-panel-arrow { opacity: 1 !important; top: calc(100% + 16px) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => (
            <div
              key={panel.id}
              {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
              className="brixsa-panel relative overflow-hidden cursor-pointer"
              style={{
                flex: '1 1 0%',
                minHeight: 700,
                backgroundColor: '#141414',
                color: 'var(--color-background, #e1e1e1)',
              }}
            >
              {/* Background image */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                src={panel.image}
                alt={panel.title}
                className="brixsa-panel-img absolute inset-0 w-full h-full object-cover"
                style={{ transition: 'transform 0.7s ease' }}
              />

              {/* Overlay */}
              <div
                {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                className="brixsa-panel-overlay absolute inset-0"
                style={{
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  zIndex: 1,
                  transition: 'background-color 0.4s',
                }}
              />

              {/* Content */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ zIndex: 2 }}
              >
                <div className="relative flex flex-col items-center" style={{ gap: 40 }}>
                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: 'var(--font-sans), sans-serif',
                      fontSize: 'clamp(1.5rem, 1.0714rem + 1.9048vw, 2.5rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#ffffff',
                      textAlign: 'center',
                      margin: 0,
                    }}
                  >
                    {panel.title}
                  </h4>

                  {/* Arrow icon */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    className="brixsa-panel-arrow"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 40px)',
                      opacity: 0,
                      transition: 'top 0.5s ease, opacity 0.5s ease',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // BRIXSA — Services (Smooth Closings split layout)
  // ═══════════════════════════════════════════

  if (variant === 'brixsa-services') {
    const [activeIndex, setActiveIndex] = useState(0)

    const serviceImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultServices = [
      { title: 'Smooth Closings', description: 'Our agents are committed to ensuring a seamless transaction process, offering personalized advice and expert negotiation skills.' },
      { title: 'Local Experts', description: 'Our team of local experts provides in-depth knowledge of neighborhoods, market trends, and community insights.' },
      { title: 'Verified Listings', description: 'Every property in our portfolio undergoes thorough verification to ensure quality, accuracy, and compliance.' },
      { title: 'Support', description: 'We provide dedicated support throughout your entire real estate journey, from first inquiry to final closing.' },
    ]

    const resolvedItems = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultServices[i % defaultServices.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultServices[i % defaultServices.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || serviceImages[i % serviceImages.length],
          id: item.id,
        }))
      : defaultServices.map((s, i) => ({ ...s, image: serviceImages[i], id: `default-${i}` }))

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Expertise Section')} style={{ fontFamily: 'var(--font-body, inherit)' }}>
        {/* Split layout */}
        <div {...elementProps(config.id, 'splitLayout', 'container', 'Split Layout')} style={{ display: 'flex', minHeight: 650, overflow: 'hidden' }}>
          {/* Left side — Image with crossfade */}
          <div {...elementProps(config.id, 'imagePanel', 'container', 'Image Panel')} style={{ position: 'relative', width: '50%', overflow: 'hidden' }}>
            {resolvedItems.map((item, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={item.id}
                src={item.image}
                alt={item.title}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: activeIndex === i ? 1 : 0,
                  transition: 'opacity 0.6s ease',
                }}
              />
            ))}
            {/* Featured badge */}
            <div
              {...elementProps(config.id, 'featuredBadge', 'badge')}
              style={{
                position: 'absolute',
                top: 24,
                left: 24,
                zIndex: 2,
                padding: '8px 20px',
                borderRadius: 4,
                backdropFilter: 'blur(30px)',
                WebkitBackdropFilter: 'blur(30px)',
                background: 'rgba(158, 158, 158, 0.3)',
                color: '#ffffff',
                fontSize: 16,
                fontWeight: 400,
              }}
            >
              Featured
            </div>
          </div>

          {/* Right side — Dark panel with service names */}
          <div
            {...elementProps(config.id, 'contentPanel', 'container', 'Content Panel')}
            style={{
              width: '50%',
              backgroundColor: '#141414',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '60px 60px 60px 80px',
              position: 'relative',
            }}
          >
            {/* Service names */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: -20 }}>
              {resolvedItems.map((item, i) => {
                const isActive = activeIndex === i
                return (
                  <h3
                    key={item.id}
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    onMouseEnter={() => setActiveIndex(i)}
                    style={{
                      fontFamily: 'var(--font-sans), sans-serif',
                      fontSize: 'clamp(1.25rem, 1rem + 1.2vw, 2rem)',
                      fontWeight: 500,
                      lineHeight: '130%',
                      color: isActive ? '#ffffff' : 'var(--color-background, #e1e1e1)',
                      opacity: isActive ? 1 : 0.4,
                      cursor: 'pointer',
                      margin: 0,
                      padding: '6px 0',
                      transition: 'opacity 0.4s ease, color 0.4s ease',
                    }}
                  >
                    {item.title}
                  </h3>
                )
              })}
            </div>

            {/* Description — fixed height zone, absolutely positioned at bottom so it never pushes titles */}
            <div style={{ position: 'absolute', bottom: 60, left: 80, right: 60, height: 80 }}>
              {resolvedItems.map((item, i) => (
                <p
                  key={item.id}
                  {...elementProps(config.id, `items.${i}.description`, 'text')}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    fontFamily: 'var(--font-body, inherit)',
                    fontSize: 16,
                    lineHeight: '170%',
                    color: 'var(--color-background, #e1e1e1)',
                    opacity: activeIndex === i ? 0.7 : 0,
                    margin: 0,
                    maxWidth: 420,
                    transition: 'opacity 0.4s ease',
                  }}
                >
                  {item.description}
                </p>
              ))}
            </div>

            {/* View circle button */}
            <div
              {...elementProps(config.id, 'viewButton', 'button')}
              style={{
                position: 'absolute',
                bottom: 40,
                right: 60,
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '1.5px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.3s ease, background-color 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <span style={{ color: '#ffffff', fontSize: 13, fontWeight: 500, fontFamily: 'var(--font-sans), sans-serif' }}>View</span>
            </div>
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // BRIXSA — Location (Cities showcase)
  // ═══════════════════════════════════════════

  if (variant === 'brixsa-location') {
    const [activeCity, setActiveCity] = useState<number | null>(null)

    const cityImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultCities = ['Birmingham', 'San Francisco', 'Miami', 'Chicago', 'Los Angeles', 'New York City']
    const cities = items.length > 0 ? items.map(item => item.title) : defaultCities

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Locations Section')}
        style={{
          backgroundColor: '#141414',
          color: 'var(--color-background, #e1e1e1)',
          position: 'relative',
          padding: '220px 60px',
          fontFamily: 'var(--font-body, inherit)',
        }}
      >
        {/* Cities wrapper */}
        <div
          {...elementProps(config.id, 'citiesGrid', 'container', 'Cities Grid')}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxWidth: 1096,
            margin: '0 auto',
            gap: 24,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {cities.map((city, i) => (
            <div
              key={items[i]?.id ?? `city-${i}`}
              style={{ display: 'flex', gap: 24, alignItems: 'center' }}
            >
              <div
                style={{ position: 'relative' }}
                onMouseEnter={() => setActiveCity(i)}
                onMouseLeave={() => setActiveCity(null)}
              >
                {/* Hover image above city name */}
                <img
                  {...elementProps(config.id, `items.${i}.image`, 'image', 'City Image')}
                  src={cityImages[i % cityImages.length]}
                  alt=""
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    width: 120,
                    aspectRatio: '3/4',
                    objectFit: 'cover',
                    borderRadius: 6,
                    pointerEvents: 'none',
                    opacity: activeCity === i ? 1 : 0,
                    transform: activeCity === i ? 'translateX(-50%) scale(1)' : 'translateX(-50%) scale(0.8)',
                    transition: 'opacity 0.35s ease, transform 0.35s ease',
                    marginBottom: 8,
                    zIndex: 10,
                  }}
                />
                <h2
                  {...elementProps(config.id, `items.${i}.title`, 'heading')}
                  style={{
                    fontFamily: 'var(--font-sans), sans-serif',
                    fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                    fontWeight: 500,
                    lineHeight: '100%',
                    margin: 0,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '5px',
                    textDecorationThickness: '3px',
                    textDecorationColor: activeCity === i ? 'var(--color-background, #e1e1e1)' : 'transparent',
                    transition: 'text-decoration-color 0.5s ease',
                    color: 'var(--color-background, #e1e1e1)',
                  }}
                >
                  {city}
                </h2>
              </div>
              {i < cities.length - 1 && (
                <span
                  {...elementProps(config.id, `divider.${i}`, 'text', 'Divider')}
                  style={{
                    fontFamily: 'var(--font-sans), sans-serif',
                    fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                    fontWeight: 500,
                    lineHeight: '100%',
                    opacity: 0.1,
                    color: 'var(--color-background, #e1e1e1)',
                  }}
                >
                  /
                </span>
              )}
            </div>
          ))}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════════════════
  // ZMR-EVENTS — Grille événements style ZMR : fond noir, cards 16:9,
  // date badge blanc, type badge, titre, lieu, heure, prix, CTA "Réserver"
  // ═══════════════════════════════════════════════════════
  if (variant === 'zmr-events') {
    return (
      <section
        style={{ fontFamily: 'var(--font-body, inherit)', background: '#000', paddingTop: '200px', paddingBottom: '80px' }}
      >
        {/* Title */}
        {content.title && (
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            className="text-center mb-4"
            style={{ color: 'white', fontSize: 'clamp(20px, 4vw, 40px)', fontWeight: 400, letterSpacing: '0.15em', textTransform: 'uppercase' }}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && (
          <p
            {...elementProps(config.id, 'subtitle', 'text')}
            className="text-center mb-12"
            style={{ color: 'rgba(255,255,255,0.5)', fontSize: '16px', fontWeight: 400 }}
          >
            {content.subtitle}
          </p>
        )}

        {/* Events Grid */}
        <div
          className="max-w-[1400px] mx-auto px-10 grid gap-10"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}
        >
          {items.map((item, i) => (
            <div
              key={item.id}
              className="rounded-lg overflow-hidden transition-transform duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)]"
              style={{ background: '#111', cursor: 'pointer' }}
            >
              {/* Image 16:9 */}
              <div className="relative" style={{ aspectRatio: '16/9', overflow: 'hidden' }}>
                {item.icon && item.icon.startsWith('http') ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    {...elementProps(config.id, `items.${i}.icon`, 'image')}
                    src={item.icon}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                    <span className="text-4xl">{item.icon || '📅'}</span>
                  </div>
                )}
                {/* Date Badge */}
                <div className="absolute top-4 left-4 bg-white text-black px-4 py-3 rounded text-center" style={{ minWidth: '60px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, lineHeight: 1 }}>
                    {item.title?.match(/\d+/)?.[0] || '15'}
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 500, letterSpacing: '0.05em' }}>
                    {item.title?.match(/[A-Z]{3}/)?.[0] || 'JAN'}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3
                  {...elementProps(config.id, `items.${i}.title`, 'heading')}
                  style={{ color: 'white', fontSize: '22px', fontWeight: 500, marginBottom: '12px', letterSpacing: '0.02em' }}
                >
                  {item.title}
                </h3>
                <p
                  {...elementProps(config.id, `items.${i}.description`, 'text')}
                  className="flex items-center gap-4 mb-3"
                  style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}
                >
                  {item.description}
                </p>
                {/* Footer with CTA */}
                <div className="flex justify-between items-center mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <span style={{ color: 'white', fontSize: '18px', fontWeight: 600 }}>
                    {item.icon === 'free' ? 'GRATUIT' : 'Voir détails'}
                  </span>
                  <span
                    className="px-4 py-2 text-xs font-medium tracking-[0.1em] uppercase transition-all"
                    style={{ border: '1px solid white', borderRadius: '4px', color: 'white' }}
                  >
                    RÉSERVER
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════════════════
  // ZMR-MEASUREMENTS — Barre de mensurations talent : type badges (pills border #444),
  // mesures en row horizontale (13px uppercase, letter-spacing 0.1em), fond #0a0a0a
  // ═══════════════════════════════════════════════════════
  if (variant === 'zmr-measurements') {
    // Items: each item.title = label (e.g. "HEIGHT"), item.description = value (e.g. "178cm")
    // item.icon used as type badge text (e.g. "MODEL", "ACTING")
    const typeBadges = items.filter(it => it.icon && !it.icon.startsWith('http')).map(it => it.icon)
    const measurements = items.filter(it => it.title && it.description)
    return (
      <section
        className="measurements-section"
        style={{
          fontFamily: 'var(--font-body, inherit)',
          background: '#0a0a0a',
          padding: '20px 40px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {/* Type Badges */}
        {typeBadges.length > 0 && (
          <div
            className="type-badges"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '16px',
            }}
          >
            {typeBadges.map((badge, i) => (
              <span
                key={i}
                className="type-badge"
                style={{
                  padding: '4px 16px',
                  border: '1px solid #444',
                  borderRadius: '999px',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 500,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                }}
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Measurements Row */}
        <div
          className="measurements-list"
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '8px 24px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.7)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {measurements.map((item, i) => (
            <div key={item.id} style={{ display: 'flex', gap: '6px' }}>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
                {item.title}
              </span>
              <span
                {...elementProps(config.id, `items.${i}.description`, 'text')}
                style={{ color: 'white', fontWeight: 500 }}
              >
                {item.description}
              </span>
            </div>
          ))}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════════════════
  // ZMR-EXPERIENCE — Grille expériences talent : auto-fill 280px, 4:3 images,
  // type badge blanc, titre/brand/role/year, fond #0a0a0a
  // ═══════════════════════════════════════════════════════
  if (variant === 'zmr-experience') {
    return (
      <section
        className="section-work"
        style={{
          fontFamily: 'var(--font-body, inherit)',
          background: '#0a0a0a',
          padding: '60px 40px',
        }}
      >
        {content.title && (
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            className="text-center mb-10"
            style={{
              color: 'white',
              fontSize: 'clamp(22px, 3.5vw, 50px)',
              fontWeight: 900,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            {content.title}
          </h2>
        )}

        <div
          className="max-w-[1400px] mx-auto grid gap-6"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
        >
          {items.map((item, i) => (
            <div
              key={item.id}
              className="overflow-hidden transition-transform duration-300 hover:-translate-y-1"
              style={{ background: '#111', borderRadius: '4px', cursor: 'pointer' }}
            >
              {/* Image 4:3 */}
              <div className="relative" style={{ aspectRatio: '4/3', overflow: 'hidden' }}>
                {item.icon && item.icon.startsWith('http') ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    {...elementProps(config.id, `items.${i}.icon`, 'image')}
                    src={item.icon}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                    <span className="text-4xl">{item.icon || '📸'}</span>
                  </div>
                )}
                {/* Type Badge */}
                <div
                  className="absolute top-3 left-3 px-3 py-1 rounded text-xs font-semibold uppercase"
                  style={{
                    background: 'white',
                    color: '#000',
                    fontSize: '10px',
                    letterSpacing: '0.1em',
                  }}
                >
                  {item.description?.split('|')[0]?.trim() || 'SHOW'}
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3
                  {...elementProps(config.id, `items.${i}.title`, 'heading')}
                  style={{
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 600,
                    margin: 0,
                    marginBottom: '6px',
                    letterSpacing: '0.02em',
                  }}
                >
                  {item.title}
                </h3>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '13px',
                    fontWeight: 400,
                    margin: 0,
                  }}
                >
                  {item.description?.split('|').slice(1).join(' · ').trim() || ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // SERENITE — Horizontal Accordion (Spa / Beauty Institute)
  // ═══════════════════════════════════════════

  if (variant === 'serenite-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Soins Visage', description: 'Des protocoles sur mesure utilisant des produits d\'exception pour révéler l\'éclat naturel de votre peau et ralentir les signes du temps.' },
      { title: 'Soins Corps', description: 'Gommages, enveloppements et rituels corporels pensés pour sculpter, hydrater et régénérer votre corps en profondeur.' },
      { title: 'Massages', description: 'Du massage relaxant au deep tissue, nos praticiens certifiés adaptent chaque séance à vos besoins pour une détente absolue.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#1B1B2F', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#D4B896',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .serenite-panel-collapsed:hover { background-color: rgba(27, 27, 47, 0.85) !important; }
          .serenite-panel-expanded:hover .serenite-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'serenite-panel-expanded' : 'serenite-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#1B1B2F',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="serenite-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(27, 27, 47, 0.92) 0%, rgba(27, 27, 47, 0.4) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Glassmorphism overlay strip (collapsed) */}
                {!isExpanded && (
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(27, 27, 47, 0.6)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Gold number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#D4B896',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.7,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  {/* Gold number — expanded */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      color: '#D4B896',
                      marginBottom: 12,
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    0{i + 1}
                  </span>

                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#7B6F8A',
                      opacity: 0.95,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Gold glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(212, 184, 150, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(212, 184, 150, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(212, 184, 150, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(212, 184, 150, 0.2)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#D4B896" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // DJ PULSE — Horizontal Accordion (DJ / Musician)
  // ═══════════════════════════════════════════

  if (variant === 'pulse-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'DJ Set', description: 'Des sets sur mesure qui fusionnent les genres et enflamment les dancefloors. De la deep house au techno, chaque mix est une expérience unique.' },
      { title: 'Production', description: 'Compositions originales, remixes officiels et sound design. Un studio équipé pour donner vie à vos projets musicaux les plus ambitieux.' },
      { title: 'Live Act', description: 'Performance live avec synthétiseurs, drum machines et effets visuels synchronisés pour une expérience audiovisuelle immersive.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#0D0D0D', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#00E5FF',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .pulse-panel-collapsed:hover { background-color: rgba(13, 13, 13, 0.85) !important; }
          .pulse-panel-expanded:hover .pulse-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'pulse-panel-expanded' : 'pulse-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#0D0D0D',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="pulse-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(13, 13, 13, 0.92) 0%, rgba(13, 13, 13, 0.4) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Glassmorphism overlay strip (collapsed) */}
                {!isExpanded && (
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(13, 13, 13, 0.6)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Cyan number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#00E5FF',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.7,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  {/* Cyan number — expanded */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      color: '#00E5FF',
                      marginBottom: 12,
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    0{i + 1}
                  </span>

                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: 'rgba(255, 255, 255, 0.6)',
                      opacity: 0.95,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Magenta glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 0, 110, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 0, 110, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 0, 110, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 0, 110, 0.2)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#FF006E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // TRAITEUR SAVEUR — Horizontal Accordion (Caterer / Private Chef)
  // ═══════════════════════════════════════════

  if (variant === 'saveur-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Cocktails & Réceptions', description: 'Des cocktails dînatoires raffinés aux grandes réceptions, nous créons des expériences culinaires mémorables pour vos moments les plus précieux.' },
      { title: 'Repas à domicile', description: 'Votre chef privé s\'installe dans votre cuisine pour un dîner d\'exception. Menu personnalisé, produits frais et service impeccable.' },
      { title: 'Événements d\'entreprise', description: 'Séminaires, lancements de produit, galas : une offre traiteur haut de gamme adaptée à l\'image de votre entreprise.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#1C1917', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#C8A97E',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .saveur-panel-collapsed:hover { background-color: rgba(28, 25, 23, 0.85) !important; }
          .saveur-panel-expanded:hover .saveur-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'saveur-panel-expanded' : 'saveur-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#1C1917',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="saveur-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(28, 25, 23, 0.93) 0%, rgba(28, 25, 23, 0.4) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Glassmorphism overlay strip (collapsed) */}
                {!isExpanded && (
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(28, 25, 23, 0.6)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Gold number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#C8A97E',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.7,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  {/* Gold number — expanded */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      color: '#C8A97E',
                      marginBottom: 12,
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    0{i + 1}
                  </span>

                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#6B4C3B',
                      opacity: 0.95,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Gold glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(200, 169, 126, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(200, 169, 126, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(200, 169, 126, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(200, 169, 126, 0.2)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#C8A97E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // COACH ASCENT — Business / Life Coach Horizontal Accordion
  // ═══════════════════════════════════════════

  if (variant === 'ascent-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Coaching Individuel', description: 'Un accompagnement personnalisé pour clarifier votre vision, dépasser vos blocages et atteindre vos objectifs professionnels et personnels.' },
      { title: 'Coaching d\'Équipe', description: 'Renforcez la cohésion, développez le leadership et alignez votre équipe autour d\'une vision commune pour des résultats exceptionnels.' },
      { title: 'Conférences & Keynotes', description: 'Des interventions inspirantes et percutantes pour motiver vos équipes, transformer les mindsets et impulser le changement.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#111827', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#E0B870',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .ascent-panel-collapsed:hover { background-color: rgba(17, 24, 39, 0.85) !important; }
          .ascent-panel-expanded:hover .ascent-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'ascent-panel-expanded' : 'ascent-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#111827',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="ascent-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(17, 24, 39, 0.92) 0%, rgba(17, 24, 39, 0.45) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Glassmorphism overlay strip (collapsed) */}
                {!isExpanded && (
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(17, 24, 39, 0.65)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Gold number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#E0B870',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.7,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  {/* Gold number — expanded */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      color: '#E0B870',
                      marginBottom: 12,
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    0{i + 1}
                  </span>

                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: 'rgba(255, 255, 255, 0.75)',
                      opacity: 0.95,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Gold glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(224, 184, 112, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(224, 184, 112, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(224, 184, 112, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(224, 184, 112, 0.2)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#E0B870" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // YOGA ZENITH — Horizontal Accordion (Yoga / Pilates Studio)
  // ═══════════════════════════════════════════════════════════

  if (variant === 'zenith-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Yoga Vinyasa', description: 'Un flow dynamique qui synchronise mouvement et respiration. Renforcez votre corps, libérez votre esprit et développez une souplesse profonde.' },
      { title: 'Pilates Mat', description: 'Des exercices au sol ciblés pour sculpter votre silhouette, renforcer votre centre et améliorer votre posture au quotidien.' },
      { title: 'Méditation & Yin', description: 'Ralentissez, respirez et reconnectez-vous à vous-même. Des pratiques douces pour apaiser le mental et relâcher les tensions profondes.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#1A1A1A', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#A8C5A0',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .zenith-panel-collapsed:hover { background-color: rgba(26, 26, 26, 0.85) !important; }
          .zenith-panel-expanded:hover .zenith-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'zenith-panel-expanded' : 'zenith-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#1A1A1A',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="zenith-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(26, 26, 26, 0.92) 0%, rgba(26, 26, 26, 0.45) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Glassmorphism overlay strip (collapsed) */}
                {!isExpanded && (
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(26, 26, 26, 0.65)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Sage number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#A8C5A0',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.7,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  {/* Sage number — expanded */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      color: '#A8C5A0',
                      marginBottom: 12,
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    0{i + 1}
                  </span>

                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#D4C5B0',
                      opacity: 0.95,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Sage glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(168, 197, 160, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(168, 197, 160, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(168, 197, 160, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(168, 197, 160, 0.2)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#A8C5A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // PATISSERIE MIEL — Horizontal Accordion (Pastry Shop / Bakery)
  // ═══════════════════════════════════════════

  if (variant === 'miel-accordion') {
    const [activePanel, setActivePanel] = useState(0)

    const scrollRevealRef = (el: HTMLDivElement | null) => {
      if (!el) return
      el.style.opacity = '0'
      el.style.transform = 'translateY(40px)'
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
          obs.disconnect()
        }
      }, { threshold: 0.15 })
      obs.observe(el)
    }

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Pâtisserie Fine', description: 'Des créations d\'exception alliant techniques françaises et inspirations du monde. Chaque gâteau est une œuvre d\'art comestible, réalisée avec des ingrédients d\'excellence.' },
      { title: 'Boulangerie Artisanale', description: 'Farines bio, levain naturel et cuisson au four à pierre. Notre pain est pétri à la main chaque matin pour vous offrir des saveurs authentiques.' },
      { title: 'Traiteur Sucré', description: 'Mariages, événements et réceptions : nous créons des buffets sucrés spectaculaires et des pièces montées sur mesure pour vos moments inoubliables.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        ref={scrollRevealRef}
        className="overflow-hidden"
        style={{ backgroundColor: '#2A1F1A', fontFamily: "var(--font-sans), sans-serif" }}
      >
        {/* Header area */}
        <div {...elementProps(config.id, 'headerArea', 'container', 'Header Area')} style={{ padding: '0 60px', paddingTop: 'clamp(60px, 10vw, 120px)' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  marginBottom: 60,
                  color: customTextColor ?? '#E8C17A',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* 3 horizontal accordion panels */}
        <style>{`
          .miel-panel-collapsed:hover { background-color: rgba(42, 31, 26, 0.85) !important; }
          .miel-panel-expanded:hover .miel-panel-img { transform: scale(1.04) !important; }
        `}</style>
        <div
          {...elementProps(config.id, 'panelsRow', 'container', 'Panels Row')}
          className="flex flex-row"
          style={{ minHeight: 700 }}
        >
          {resolvedPanels.map((panel, i) => {
            const isExpanded = activePanel === i

            return (
              <div
                key={panel.id}
                {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                className={cn(
                  'relative overflow-hidden cursor-pointer',
                  isExpanded ? 'miel-panel-expanded' : 'miel-panel-collapsed'
                )}
                style={{
                  flex: isExpanded ? '3 1 0%' : '0.5 1 0%',
                  minHeight: 700,
                  backgroundColor: '#2A1F1A',
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onClick={() => setActivePanel(i)}
              >
                {/* Background image — visible when expanded */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  {...elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image')}
                  src={panel.image}
                  alt={panel.title}
                  className="miel-panel-img absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease',
                  }}
                />

                {/* Gradient overlay — expanded */}
                <div
                  {...elementProps(config.id, `panels.${i}.overlay`, 'container', 'Overlay')}
                  className="absolute inset-0"
                  style={{
                    background: isExpanded
                      ? 'linear-gradient(to top, rgba(42, 31, 26, 0.93) 0%, rgba(42, 31, 26, 0.4) 40%, transparent 70%)'
                      : 'none',
                    zIndex: 1,
                    transition: 'opacity 0.5s ease',
                  }}
                />

                {/* Glassmorphism overlay strip (collapsed) */}
                {!isExpanded && (
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(42, 31, 26, 0.6)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Collapsed state — number + vertical text */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: isExpanded ? 'none' : 'auto',
                  }}
                >
                  {/* Honey number */}
                  <span
                    {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(3rem, 2rem + 4vw, 6rem)',
                      fontWeight: 400,
                      color: '#E8C17A',
                      lineHeight: 1,
                      marginBottom: 32,
                      opacity: 0.7,
                    }}
                  >
                    0{i + 1}
                  </span>

                  {/* Vertical text */}
                  <span
                    {...elementProps(config.id, `panels.${i}.verticalTitle`, 'text', 'Vertical Title')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: '#FFFFFF',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    {panel.title}
                  </span>
                </div>

                {/* Expanded state — title + description + arrow */}
                <div
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{
                    zIndex: 2,
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    pointerEvents: isExpanded ? 'auto' : 'none',
                    padding: 'clamp(30px, 5vw, 60px)',
                  }}
                >
                  {/* Honey number — expanded */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      letterSpacing: '3px',
                      color: '#E8C17A',
                      marginBottom: 12,
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    0{i + 1}
                  </span>

                  <h4
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 'clamp(1.75rem, 1.2rem + 2.4vw, 3rem)',
                      fontWeight: 500,
                      textTransform: 'capitalize' as const,
                      color: '#FFFFFF',
                      margin: 0,
                      marginBottom: 12,
                    }}
                  >
                    {panel.title}
                  </h4>

                  <p
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    style={{
                      fontFamily: "var(--font-sans), sans-serif",
                      fontSize: 15,
                      fontWeight: 400,
                      color: '#8B5E3C',
                      opacity: 0.95,
                      margin: 0,
                      maxWidth: 400,
                      lineHeight: '160%',
                      marginBottom: 24,
                    }}
                  >
                    {panel.description}
                  </p>

                  {/* Honey glassmorphism arrow button */}
                  <div
                    {...elementProps(config.id, `panels.${i}.arrow`, 'icon', 'Arrow Icon')}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(232, 193, 122, 0.2)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(232, 193, 122, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(232, 193, 122, 0.45)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(232, 193, 122, 0.2)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="#E8C17A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  // ─── VARIANT: prisme-accordion ───
  // Opticien premium : split layout accordion, ice blue progress bar, image crossfade with zoom, auto-advance
  if (variant === 'prisme-accordion') {
    const PRISME_AUTO_DURATION = 7000
    /* eslint-disable react-hooks/rules-of-hooks */
    const [activePanel, setActivePanel] = useState(0)
    const [progressKey, setProgressKey] = useState(0)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const sectionRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(false)
    const [hasRevealed, setHasRevealed] = useState(false)

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Examen de vue', description: 'Un bilan visuel complet avec les dernières technologies de mesure pour une correction optimale et personnalisée.' },
      { title: 'Montures design', description: 'Une sélection curatée de créateurs internationaux. Des montures qui allient style, confort et technicité.' },
      { title: 'Verres progressifs', description: 'La technologie de pointe au service de votre confort. Des verres sur-mesure pour une vision parfaite à toutes les distances.' },
      { title: 'Lentilles de contact', description: 'Adaptation professionnelle et suivi personnalisé pour des lentilles parfaitement adaptées à vos yeux et votre mode de vie.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    const advancePanel = useCallback(() => {
      setActivePanel(prev => (prev + 1) % resolvedPanels.length)
      setProgressKey(k => k + 1)
    }, [resolvedPanels.length])

    const selectPanel = useCallback((i: number) => {
      setActivePanel(i)
      setProgressKey(k => k + 1)
      if (timerRef.current) clearTimeout(timerRef.current)
    }, [])

    // Auto-advance timer
    useEffect(() => {
      if (isEditing || !isVisible) return
      timerRef.current = setTimeout(advancePanel, PRISME_AUTO_DURATION)
      return () => { if (timerRef.current) clearTimeout(timerRef.current) }
    }, [activePanel, progressKey, advancePanel, isEditing, isVisible])

    // Scroll reveal observer
    useEffect(() => {
      const el = sectionRef.current
      if (!el) return
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          if (!hasRevealed) setHasRevealed(true)
        } else {
          setIsVisible(false)
        }
      }, { threshold: 0.15 })
      obs.observe(el)
      return () => obs.disconnect()
    }, [hasRevealed])
    /* eslint-enable react-hooks/rules-of-hooks */

    const staggerDelay = (i: number) => `${0.12 * i}s`

    return (
      <section
        ref={sectionRef}
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        className="overflow-hidden"
        style={{ backgroundColor: '#E8DED0', fontFamily: "var(--font-sans), sans-serif" }}
      >
        <style>{`
          @keyframes prisme-progress-fill { from { width: 0%; } to { width: 100%; } }
          @keyframes prisme-reveal-up { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes prisme-img-in { from { opacity: 0; transform: scale(1.08); } to { opacity: 1; transform: scale(1); } }
          .prisme-acc-panel:hover .prisme-acc-glass {
            background: rgba(184, 212, 227, 0.06) !important;
          }
          .prisme-acc-panel.prisme-acc-active:hover .prisme-acc-glass {
            background: transparent !important;
          }
          @media (max-width: 968px) {
            .prisme-acc-split { flex-direction: column !important; }
            .prisme-acc-image-col { width: 100% !important; min-height: 400px !important; }
            .prisme-acc-content-col { width: 100% !important; }
          }
        `}</style>

        {/* Header area — staggered reveal */}
        <div
          {...elementProps(config.id, 'headerArea', 'container', 'Header Area')}
          style={{
            padding: 'clamp(60px, 10vw, 120px) 60px clamp(40px, 6vw, 80px)',
            opacity: hasRevealed ? 1 : 0,
            transform: hasRevealed ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 400,
                  lineHeight: '110%',
                  color: customTextColor ?? '#0F1923',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* Split layout: image left + accordion right */}
        <div
          {...elementProps(config.id, 'splitLayout', 'container', 'Split Layout')}
          className="prisme-acc-split"
          style={{
            display: 'flex',
            minHeight: 640,
            maxWidth: 1320,
            margin: '0 auto',
            padding: '0 clamp(20px, 4vw, 60px)',
            paddingBottom: 'clamp(60px, 10vw, 120px)',
            gap: 0,
            opacity: hasRevealed ? 1 : 0,
            transform: hasRevealed ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
          }}
        >
          {/* Left — Image with crossfade + zoom */}
          <div
            {...elementProps(config.id, 'imagePanel', 'container', 'Image Panel')}
            className="prisme-acc-image-col"
            style={{ position: 'relative', width: '50%', overflow: 'hidden', borderRadius: '12px 0 0 12px', backgroundColor: '#0F1923' }}
          >
            {resolvedPanels.map((panel, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={`${panel.id}-${i}`}
                {...(activePanel === i ? elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image') : {})}
                src={panel.image}
                alt={panel.title}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: activePanel === i ? 1 : 0,
                  transform: activePanel === i ? 'scale(1)' : 'scale(1.08)',
                  transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                }}
              />
            ))}
            {/* Subtle gradient overlay on image */}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 1,
              background: 'linear-gradient(135deg, rgba(15,25,35,0.15) 0%, transparent 60%)',
              pointerEvents: 'none',
            }} />
            {/* Geometric decorative lines (optician precision) */}
            <svg style={{ position: 'absolute', bottom: 24, left: 24, zIndex: 2, opacity: 0.3 }} width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="38" stroke="#B8D4E3" strokeWidth="0.5" />
              <circle cx="40" cy="40" r="28" stroke="#B8D4E3" strokeWidth="0.5" />
              <line x1="2" y1="40" x2="78" y2="40" stroke="#B8D4E3" strokeWidth="0.3" />
              <line x1="40" y1="2" x2="40" y2="78" stroke="#B8D4E3" strokeWidth="0.3" />
            </svg>
          </div>

          {/* Right — Accordion panels */}
          <div
            {...elementProps(config.id, 'accordionCol', 'container', 'Accordion Column')}
            className="prisme-acc-content-col"
            style={{
              width: '50%',
              backgroundColor: '#0F1923',
              borderRadius: '0 12px 12px 0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: 'clamp(32px, 5vw, 56px)',
            }}
          >
            {resolvedPanels.map((panel, i) => {
              const isActive = activePanel === i
              return (
                <div
                  key={panel.id}
                  {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                  className={cn('prisme-acc-panel', isActive && 'prisme-acc-active')}
                  onClick={() => selectPanel(i)}
                  style={{
                    cursor: 'pointer',
                    position: 'relative',
                    borderBottom: i < resolvedPanels.length - 1 ? '1px solid rgba(184, 212, 227, 0.1)' : 'none',
                    padding: isActive ? '24px 0 28px' : '20px 0',
                    transition: 'padding 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: hasRevealed ? 1 : 0,
                    transform: hasRevealed ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: hasRevealed ? staggerDelay(i + 2) : '0s',
                    transitionProperty: 'opacity, transform, padding',
                    transitionDuration: '0.6s, 0.6s, 0.5s',
                  }}
                >
                  {/* Glass highlight overlay */}
                  <div
                    className="prisme-acc-glass"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 8,
                      background: 'transparent',
                      transition: 'background 0.4s ease',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Header row: number + title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
                    <span
                      {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                      style={{
                        fontFamily: "var(--font-sans), sans-serif",
                        fontSize: 'clamp(1.5rem, 1rem + 1.5vw, 2.25rem)',
                        fontWeight: 200,
                        color: '#B8D4E3',
                        opacity: isActive ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        minWidth: 48,
                      }}
                    >
                      0{i + 1}
                    </span>
                    <h4
                      {...elementProps(config.id, `items.${i}.title`, 'heading')}
                      style={{
                        fontFamily: "var(--font-sans), sans-serif",
                        fontSize: 'clamp(1.1rem, 0.9rem + 1vw, 1.5rem)',
                        fontWeight: isActive ? 400 : 300,
                        color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
                        margin: 0,
                        transition: 'color 0.4s ease, font-weight 0.4s ease',
                        flex: 1,
                      }}
                    >
                      {panel.title}
                    </h4>
                    {/* Expand indicator */}
                    <svg
                      width="20" height="20" viewBox="0 0 20 20" fill="none"
                      style={{
                        transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: isActive ? 0.8 : 0.3,
                        flexShrink: 0,
                      }}
                    >
                      <path d="M5 8l5 5 5-5" stroke="#B8D4E3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>

                  {/* Collapsible description */}
                  <div
                    style={{
                      maxHeight: isActive ? 200 : 0,
                      overflow: 'hidden',
                      transition: 'max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
                      opacity: isActive ? 1 : 0,
                    }}
                  >
                    <p
                      {...elementProps(config.id, `items.${i}.description`, 'text')}
                      style={{
                        fontFamily: "var(--font-sans), sans-serif",
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'rgba(184, 212, 227, 0.6)',
                        margin: 0,
                        paddingTop: 12,
                        paddingLeft: 64,
                        maxWidth: 420,
                        lineHeight: '170%',
                      }}
                    >
                      {panel.description}
                    </p>
                  </div>

                  {/* Animated ice blue progress bar */}
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      backgroundColor: 'rgba(184, 212, 227, 0.1)',
                      overflow: 'hidden',
                    }}>
                      <div
                        key={`progress-${activePanel}-${progressKey}`}
                        style={{
                          height: '100%',
                          backgroundColor: '#B8D4E3',
                          animation: isEditing ? 'none' : `prisme-progress-fill ${PRISME_AUTO_DURATION}ms linear forwards`,
                          width: isEditing ? '60%' : undefined,
                        }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>
    )
  }

  // ─── VARIANT: petale-accordion ───
  // Fleuriste premium : split layout accordion, rose gold progress bar, organic bloom image transition, auto-advance
  if (variant === 'petale-accordion') {
    const PETALE_AUTO_DURATION = 7000
    /* eslint-disable react-hooks/rules-of-hooks */
    const [activePanel, setActivePanel] = useState(0)
    const [progressKey, setProgressKey] = useState(0)
    const petaleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const petaleSectionRef = useRef<HTMLDivElement>(null)
    const [petaleIsVisible, setPetaleIsVisible] = useState(false)
    const [petaleHasRevealed, setPetaleHasRevealed] = useState(false)

    const panelImages = [
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
      '/demo/placeholder.svg',
    ]

    const defaultPanelData = [
      { title: 'Bouquets sur-mesure', description: 'Chaque bouquet est une cr\u00E9ation unique, compos\u00E9e selon vos envies, la saison et le message que vous souhaitez transmettre. Des fleurs fra\u00EEches s\u00E9lectionn\u00E9es avec soin.' },
      { title: '\u00C9v\u00E9nements & mariages', description: 'De la d\u00E9coration florale de votre c\u00E9r\u00E9monie aux centres de table, nous cr\u00E9ons des ambiances v\u00E9g\u00E9tales inoubliables pour vos moments les plus pr\u00E9cieux.' },
      { title: 'Abonnements floraux', description: 'Recevez chaque semaine ou chaque mois une composition fra\u00EEche et de saison, directement chez vous ou au bureau. L\u2019\u00E9l\u00E9gance au quotidien.' },
      { title: 'Livraison express', description: 'Commandez avant midi, recevez votre bouquet le jour m\u00EAme. Un service de livraison soign\u00E9 avec message personnalis\u00E9 pour surprendre vos proches.' },
    ]

    const resolvedPanels = items.length > 0
      ? items.map((item, i) => ({
          title: item.title || defaultPanelData[i % defaultPanelData.length].title,
          description: (item as unknown as Record<string, unknown>).description as string || defaultPanelData[i % defaultPanelData.length].description,
          image: (item as unknown as Record<string, unknown>).image as string || panelImages[i % panelImages.length],
          id: item.id,
        }))
      : defaultPanelData.map((data, i) => ({ ...data, image: panelImages[i], id: `default-${i}` }))

    const petaleAdvancePanel = useCallback(() => {
      setActivePanel(prev => (prev + 1) % resolvedPanels.length)
      setProgressKey(k => k + 1)
    }, [resolvedPanels.length])

    const petaleSelectPanel = useCallback((i: number) => {
      setActivePanel(i)
      setProgressKey(k => k + 1)
      if (petaleTimerRef.current) clearTimeout(petaleTimerRef.current)
    }, [])

    // Auto-advance timer
    useEffect(() => {
      if (isEditing || !petaleIsVisible) return
      petaleTimerRef.current = setTimeout(petaleAdvancePanel, PETALE_AUTO_DURATION)
      return () => { if (petaleTimerRef.current) clearTimeout(petaleTimerRef.current) }
    }, [activePanel, progressKey, petaleAdvancePanel, isEditing, petaleIsVisible])

    // Scroll reveal observer
    useEffect(() => {
      const el = petaleSectionRef.current
      if (!el) return
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setPetaleIsVisible(true)
          if (!petaleHasRevealed) setPetaleHasRevealed(true)
        } else {
          setPetaleIsVisible(false)
        }
      }, { threshold: 0.15 })
      obs.observe(el)
      return () => obs.disconnect()
    }, [petaleHasRevealed])
    /* eslint-enable react-hooks/rules-of-hooks */

    const petaleStaggerDelay = (i: number) => `${0.12 * i}s`

    return (
      <section
        ref={petaleSectionRef}
        {...elementProps(config.id, 'wrapper', 'container', 'Services Section')}
        className="overflow-hidden"
        style={{ backgroundColor: '#1A1A1A', fontFamily: "var(--font-sans), sans-serif" }}
      >
        <style>{`
          @keyframes petale-progress-fill { from { width: 0%; } to { width: 100%; } }
          .petale-acc-panel:hover .petale-acc-glass {
            background: rgba(212, 165, 116, 0.04) !important;
          }
          .petale-acc-panel.petale-acc-active:hover .petale-acc-glass {
            background: transparent !important;
          }
          @media (max-width: 968px) {
            .petale-acc-split { flex-direction: column !important; }
            .petale-acc-image-col { width: 100% !important; min-height: 400px !important; }
            .petale-acc-content-col { width: 100% !important; }
          }
        `}</style>

        {/* Header area — staggered reveal */}
        <div
          {...elementProps(config.id, 'headerArea', 'container', 'Header Area')}
          style={{
            padding: 'clamp(60px, 10vw, 120px) 60px clamp(40px, 6vw, 80px)',
            opacity: petaleHasRevealed ? 1 : 0,
            transform: petaleHasRevealed ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            {title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                className={cn(titleSize && getTitleSizeClass(titleSize))}
                style={{
                  maxWidth: 700,
                  margin: '0 auto',
                  fontFamily: "var(--font-sans), sans-serif",
                  fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
                  fontWeight: 500,
                  lineHeight: '110%',
                  textTransform: 'capitalize' as const,
                  color: customTextColor ?? '#CBB68B',
                }}
              >
                {title}
              </h2>
            )}
          </div>
        </div>

        {/* Split layout: image left + accordion right */}
        <div
          {...elementProps(config.id, 'splitLayout', 'container', 'Split Layout')}
          className="petale-acc-split"
          style={{
            display: 'flex',
            minHeight: 640,
            maxWidth: 1320,
            margin: '0 auto',
            padding: '0 clamp(20px, 4vw, 60px)',
            paddingBottom: 'clamp(60px, 10vw, 120px)',
            gap: 0,
            opacity: petaleHasRevealed ? 1 : 0,
            transform: petaleHasRevealed ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
          }}
        >
          {/* Left — Image with organic bloom crossfade */}
          <div
            {...elementProps(config.id, 'imagePanel', 'container', 'Image Panel')}
            className="petale-acc-image-col"
            style={{ position: 'relative', width: '50%', overflow: 'hidden', borderRadius: '16px 0 0 16px', backgroundColor: '#1A1A1A' }}
          >
            {resolvedPanels.map((panel, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={`${panel.id}-${i}`}
                {...(activePanel === i ? elementProps(config.id, `panels.${i}.image`, 'image', 'Panel Image') : {})}
                src={panel.image}
                alt={panel.title}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: activePanel === i ? 1 : 0,
                  transform: activePanel === i ? 'scale(1)' : 'scale(1.12)',
                  filter: activePanel === i ? 'blur(0px)' : 'blur(4px)',
                  transition: 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1), transform 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.8s ease',
                }}
              />
            ))}
            {/* Warm gradient overlay */}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 1,
              background: 'linear-gradient(135deg, rgba(26,26,26,0.1) 0%, transparent 60%, rgba(212,165,116,0.08) 100%)',
              pointerEvents: 'none',
            }} />
            {/* Botanical decorative SVG */}
            <svg style={{ position: 'absolute', bottom: 20, left: 20, zIndex: 2, opacity: 0.25 }} width="70" height="90" viewBox="0 0 70 90" fill="none">
              <path d="M35 90V45" stroke="#CBB68B" strokeWidth="0.6" />
              <path d="M35 45C25 35 10 30 5 20C15 25 28 30 35 45Z" stroke="#CBB68B" strokeWidth="0.5" fill="none" />
              <path d="M35 45C45 35 60 30 65 20C55 25 42 30 35 45Z" stroke="#CBB68B" strokeWidth="0.5" fill="none" />
              <path d="M35 60C28 52 15 48 10 40C20 44 30 50 35 60Z" stroke="#CBB68B" strokeWidth="0.5" fill="none" />
              <path d="M35 60C42 52 55 48 60 40C50 44 40 50 35 60Z" stroke="#CBB68B" strokeWidth="0.5" fill="none" />
              <circle cx="35" cy="15" r="8" stroke="#CBB68B" strokeWidth="0.5" fill="none" />
              <circle cx="35" cy="15" r="4" stroke="#CBB68B" strokeWidth="0.4" fill="none" />
            </svg>
          </div>

          {/* Right — Accordion panels */}
          <div
            {...elementProps(config.id, 'accordionCol', 'container', 'Accordion Column')}
            className="petale-acc-content-col"
            style={{
              width: '50%',
              backgroundColor: '#111111',
              borderRadius: '0 16px 16px 0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: 'clamp(32px, 5vw, 56px)',
            }}
          >
            {resolvedPanels.map((panel, i) => {
              const isActive = activePanel === i
              return (
                <div
                  key={panel.id}
                  {...elementProps(config.id, `panels.${i}`, 'container', 'Panel')}
                  className={cn('petale-acc-panel', isActive && 'petale-acc-active')}
                  onClick={() => petaleSelectPanel(i)}
                  style={{
                    cursor: 'pointer',
                    position: 'relative',
                    borderBottom: i < resolvedPanels.length - 1 ? '1px solid rgba(212, 165, 116, 0.1)' : 'none',
                    padding: isActive ? '24px 0 28px' : '20px 0',
                    transition: 'padding 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: petaleHasRevealed ? 1 : 0,
                    transform: petaleHasRevealed ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: petaleHasRevealed ? petaleStaggerDelay(i + 2) : '0s',
                    transitionProperty: 'opacity, transform, padding',
                    transitionDuration: '0.6s, 0.6s, 0.5s',
                  }}
                >
                  {/* Warm glass highlight overlay */}
                  <div
                    className="petale-acc-glass"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 8,
                      background: 'transparent',
                      transition: 'background 0.4s ease',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Header row: serif number + title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
                    <span
                      {...elementProps(config.id, `panels.${i}.number`, 'text', 'Panel Number')}
                      style={{
                        fontFamily: 'var(--font-heading, Georgia, serif)',
                        fontSize: 'clamp(1.5rem, 1rem + 1.5vw, 2.25rem)',
                        fontWeight: 400,
                        fontStyle: 'italic',
                        color: '#CBB68B',
                        opacity: isActive ? 1 : 0.4,
                        transition: 'opacity 0.4s ease',
                        minWidth: 48,
                      }}
                    >
                      0{i + 1}
                    </span>
                    <h4
                      {...elementProps(config.id, `items.${i}.title`, 'heading')}
                      style={{
                        fontFamily: "var(--font-sans), sans-serif",
                        fontSize: 'clamp(1.1rem, 0.9rem + 1vw, 1.5rem)',
                        fontWeight: isActive ? 500 : 400,
                        textTransform: 'capitalize' as const,
                        color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                        margin: 0,
                        transition: 'color 0.4s ease, font-weight 0.4s ease',
                        flex: 1,
                      }}
                    >
                      {panel.title}
                    </h4>
                    <svg
                      width="20" height="20" viewBox="0 0 20 20" fill="none"
                      style={{
                        transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: isActive ? 0.8 : 0.3,
                        flexShrink: 0,
                      }}
                    >
                      <path d="M5 8l5 5 5-5" stroke="#CBB68B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>

                  {/* Collapsible description */}
                  <div
                    style={{
                      maxHeight: isActive ? 200 : 0,
                      overflow: 'hidden',
                      transition: 'max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
                      opacity: isActive ? 1 : 0,
                    }}
                  >
                    <p
                      {...elementProps(config.id, `items.${i}.description`, 'text')}
                      style={{
                        fontFamily: "var(--font-sans), sans-serif",
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'rgba(212, 165, 116, 0.55)',
                        margin: 0,
                        paddingTop: 12,
                        paddingLeft: 64,
                        maxWidth: 420,
                        lineHeight: '170%',
                      }}
                    >
                      {panel.description}
                    </p>
                  </div>

                  {/* Animated rose gold progress bar */}
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      backgroundColor: 'rgba(212, 165, 116, 0.1)',
                      overflow: 'hidden',
                    }}>
                      <div
                        key={`progress-${activePanel}-${progressKey}`}
                        style={{
                          height: '100%',
                          background: 'linear-gradient(90deg, #CBB68B, #E8C9A8)',
                          animation: isEditing ? 'none' : `petale-progress-fill ${PETALE_AUTO_DURATION}ms linear forwards`,
                          width: isEditing ? '60%' : undefined,
                        }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // JLSTUDIO — Scroll Pin Services
  // ═══════════════════════════════════════════

  if (variant === 'jlstudio-scroll-pin') {
    const accent = accentColor ?? '#638BFF'

    const defaultSlides = [
      {
        number: '01',
        subtitle: 'Site Vitrine',
        title: 'Votre identité en ligne',
        description: 'Un site web conçu sur mesure pour refléter votre image de marque avec élégance et performance.',
        tags: ['Design responsive', 'Animations premium', 'SEO optimisé', 'CMS intégré'],
        image: '/demo/placeholder.svg',
      },
      {
        number: '02',
        subtitle: 'E-Commerce',
        title: 'Vendez sans limites',
        description: 'Une boutique en ligne performante et sécurisée, pensée pour maximiser vos conversions.',
        tags: ['Paiement sécurisé', 'Gestion de stock', 'Panier optimisé', 'Analytics avancés'],
        image: '/demo/placeholder.svg',
      },
      {
        number: '03',
        subtitle: 'Application Web',
        title: 'Outils sur mesure',
        description: 'Des applications web robustes et intuitives pour digitaliser vos processus métier.',
        tags: ['Dashboard temps réel', 'API robuste', 'Architecture scalable', 'UX intuitive'],
        image: '/demo/placeholder.svg',
      },
      {
        number: '04',
        subtitle: 'Autres Services',
        title: 'Solutions complémentaires',
        description: 'Un accompagnement complet pour tous vos besoins numériques, du conseil à la maintenance.',
        tags: ['Landing pages', 'Refonte & migration', 'Maintenance', 'Conseil technique'],
        image: '/demo/placeholder.svg',
      },
    ]

    const resolvedSlides = items.length > 0
      ? items.map((item, i) => {
          const def = defaultSlides[i % defaultSlides.length]
          const raw = item as unknown as Record<string, unknown>
          const tagsRaw = raw.features ?? raw.tags
          let parsedTags: string[] = def.tags
          if (Array.isArray(tagsRaw)) {
            parsedTags = tagsRaw as string[]
          } else if (typeof tagsRaw === 'string') {
            try { const parsed = JSON.parse(tagsRaw); if (Array.isArray(parsed)) parsedTags = parsed }
            catch { parsedTags = (tagsRaw as string).split(',').map((t: string) => t.trim()) }
          }
          // `image` peut désormais être une simple URL (ancien format) ou un
          // objet `{ src, pos }` (cadrage choisi côté CRM).
          const rawImg = imageFocal(raw.image)
          return {
            number: def.number,
            subtitle: (raw.subtitle as string) || def.subtitle,
            title: item.title || def.subtitle,
            description: (raw.description as string) || def.description,
            tags: parsedTags,
            image: rawImg.src || def.image,
            imagePos: rawImg.src ? rawImg.pos : 'center',
            id: item.id,
          }
        })
      : defaultSlides.map((s, i) => ({ ...s, id: `default-${i}`, imagePos: 'center' }))

    return (
      <section
        {...elementProps(config.id, 'wrapper', 'container', 'Scroll Pin Services')}
        style={{ fontFamily: 'var(--font-body, inherit)', backgroundColor: '#0a0a0a' }}
      >
        {resolvedSlides.map((slide, i) => (
          <div
            key={slide.id}
            {...elementProps(config.id, `slide-${i}`, 'container', `Service Slide ${slide.number}`)}
            style={{
              position: 'relative',
              width: '100%',
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {/* Background image — fixed attachment for parallax effect */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${slide.image})`,
                backgroundSize: 'cover',
                backgroundPosition: slide.imagePos,
                backgroundAttachment: 'fixed',
              }}
            />

            {/* Dark overlay */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.58)',
              }}
            />

            {/* Content */}
            <div
              style={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: 720,
                padding: '48px 24px',
                gap: 20,
              }}
            >
              {/* Big faded number */}
              <span
                style={{
                  fontSize: 'clamp(80px, 12vw, 160px)',
                  fontWeight: 800,
                  lineHeight: 1,
                  color: accent,
                  opacity: 0.12,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {slide.number}
              </span>

              {/* Subtitle */}
              <span
                {...elementProps(config.id, `items.${i}.title`, 'badge')}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: accent,
                }}
              >
                {slide.subtitle}
              </span>

              {/* Title */}
              <h2
                {...elementProps(config.id, `items.${i}.description`, 'heading')}
                style={{
                  fontSize: 'clamp(32px, 5vw, 56px)',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: '#ffffff',
                  margin: 0,
                }}
              >
                {slide.title}
              </h2>

              {/* Description */}
              <p
                {...elementProps(config.id, `items.${i}.details`, 'text')}
                style={{
                  fontSize: 'clamp(15px, 1.8vw, 18px)',
                  lineHeight: 1.7,
                  color: 'rgba(255, 255, 255, 0.65)',
                  margin: 0,
                  maxWidth: 560,
                }}
              >
                {slide.description}
              </p>

              {/* Tags */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 10,
                  marginTop: 8,
                }}
              >
                {slide.tags.map((tag, ti) => (
                  <span
                    key={ti}
                    style={{
                      padding: '8px 18px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.85)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: 999,
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      background: 'rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Progress dots — right side */}
            <div
              style={{
                position: 'absolute',
                right: 32,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                zIndex: 3,
              }}
            >
              {resolvedSlides.map((_, di) => (
                <div
                  key={di}
                  style={{
                    width: di === i ? 10 : 8,
                    height: di === i ? 10 : 8,
                    borderRadius: '50%',
                    backgroundColor: di === i ? accent : 'rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    boxShadow: di === i ? `0 0 12px ${accent}60` : 'none',
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </section>
    )
  }

  if (universe === 'warm') {
    const accent = accentColor ?? '#b4654a'
    const layout = variant.replace('warm-', '')

    const header = (
      <div className={cn("mb-16 space-y-5", textAlign ? getTextAlignClass(textAlign) : 'text-center')}>
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-block text-xs font-medium tracking-[0.15em] uppercase" style={{ color: accent }}>
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        <div className="w-12 h-px mx-auto" style={{ background: accent, opacity: 0.4 }} />
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-normal leading-tight tracking-tight", titleSize && getTitleSizeClass(titleSize))} style={{ color: customTextColor ?? '#3d2b1f', fontFamily: 'var(--font-heading, Georgia, serif)' }}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base max-w-xl mx-auto leading-relaxed" style={{ color: '#78593e' }}>{subtitle}</p>}
      </div>
    )

    if (layout === 'grid' || layout === 'minimal') {
      return (
        <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#faf7f2' }} className="py-24">
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {items.map((item, i) => (
                <div key={item.id} className="space-y-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded flex items-center justify-center" style={{ backgroundColor: `${accent}12` }}>
                    <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" style={{ color: accent }} />
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-medium text-base" style={{ color: '#3d2b1f', fontFamily: 'var(--font-heading, Georgia, serif)' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#78593e' }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#faf7f2' }} className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-0 divide-y" style={{ borderColor: `${accent}15` }}>
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-5 py-6">
                <span className="text-sm font-medium shrink-0 mt-1" style={{ color: accent }}>0{i + 1}</span>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-medium mb-1" style={{ color: '#3d2b1f', fontFamily: 'var(--font-heading, Georgia, serif)' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#78593e' }}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // PLAYFUL — Duolingo/Figma, bold rounded, vibrant
  // ═══════════════════════════════════════════

  if (universe === 'playful') {
    const accent = accentColor ?? '#7c3aed'
    const layout = variant.replace('playful-', '')

    const header = (
      <div className={cn("text-center mb-14 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2 rounded-full text-white" style={{ backgroundColor: accent }}>
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-extrabold text-zinc-900 leading-tight", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor } : undefined}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto font-medium">{subtitle}</p>}
      </div>
    )

    if (layout === 'grid' || layout === 'cards') {
      return (
        <section className="bg-white py-20" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item, i) => {
                const colors = ['#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444']
                const c = colors[i % colors.length]
                return (
                  <div key={item.id} className="rounded-2xl p-7 border-2 border-zinc-100 hover:border-violet-200 transition-all hover:shadow-lg hover:-translate-y-1" style={{ background: '#faf5ff' }}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl mb-5" style={{ backgroundColor: `${c}15` }}>
                      <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" style={{ color: c }} />
                    </div>
                    <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-bold text-zinc-900 mb-2">{item.title}</h3>
                    <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-500 leading-relaxed">{item.description}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section className="bg-white py-20" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-4">
            {items.map((item, i) => (
              <div key={item.id} className="flex items-center gap-5 p-5 rounded-2xl border-2 border-zinc-100 hover:border-violet-200 transition-all">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}12` }}>
                  <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-lg leading-none" style={{ color: accent }} />
                </div>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-bold text-zinc-900 mb-0.5">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm text-zinc-500">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // RETRO — Vintage, slab, amber, sharp edges
  // ═══════════════════════════════════════════

  if (universe === 'retro') {
    const accent = accentColor ?? '#d97706'
    const layout = variant.replace('retro-', '')

    const header = (
      <div className={cn("text-center mb-14 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        <div className="flex items-center justify-center gap-4 mb-2">
          <div className="w-12 h-0.5" style={{ backgroundColor: '#92400e' }} />
          <span style={{ color: '#92400e', fontSize: '1rem' }}>✦</span>
          <div className="w-12 h-0.5" style={{ backgroundColor: '#92400e' }} />
        </div>
        {eyebrow && <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-block text-xs font-extrabold tracking-[0.2em] uppercase px-3 py-1" style={{ backgroundColor: accent, color: '#fffbeb' }}>{eyebrow}</span>}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-black uppercase tracking-wide leading-tight", titleSize && getTitleSizeClass(titleSize))} style={{ color: customTextColor ?? '#451a03' }}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base max-w-xl mx-auto" style={{ color: '#78350f' }}>{subtitle}</p>}
      </div>
    )

    if (layout === 'grid' || layout === 'cards') {
      return (
        <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#fffbeb' }} className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item, i) => (
                <div key={item.id} className="p-6 border-2 hover:shadow-md transition-shadow" style={{ borderColor: '#92400e', background: '#fff' }}>
                  <div className="text-2xl font-black mb-4" style={{ color: accent }}>0{i + 1}</div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-extrabold uppercase tracking-wide text-sm mb-2" style={{ color: '#451a03' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#78350f' }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#fffbeb' }} className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-0 divide-y-2" style={{ borderColor: '#92400e' }}>
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-5 py-6">
                <span className="text-2xl font-black shrink-0" style={{ color: accent }}>0{i + 1}</span>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-extrabold uppercase tracking-wide text-sm mb-1" style={{ color: '#451a03' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#78350f' }}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // DARK-PREMIUM — Noir & gold, ultra-thin, cinematic
  // ═══════════════════════════════════════════

  if (variant.startsWith('dark-premium')) {
    const gold = accentColor ?? '#CBB68B'
    const layout = variant.replace('dark-premium-', '')

    const header = (
      <div className={cn("text-center mb-16 space-y-6", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow && (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-block text-[0.6875rem] font-normal tracking-[0.3em] uppercase" style={{ color: gold }}>
            {eyebrow}
          </span>
        )}
        <div className="w-16 h-px mx-auto" style={{ background: `linear-gradient(90deg, transparent, ${gold}, transparent)` }} />
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-extralight leading-tight tracking-[0.04em] uppercase text-white", titleSize && getTitleSizeClass(titleSize))} style={customTextColor ? { color: customTextColor } : undefined}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base max-w-xl mx-auto font-normal tracking-wide" style={{ color: `${gold}80` }}>{subtitle}</p>}
      </div>
    )

    if (layout === 'grid' || layout === 'cards') {
      return (
        <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#0a0a0a' }} className="py-24">
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item, i) => (
                <div key={item.id} className="p-7 border transition-colors hover:border-opacity-40" style={{ borderColor: `${gold}20`, background: 'rgba(255,255,255,0.02)' }}>
                  <div className="w-10 h-10 flex items-center justify-center mb-5 border" style={{ borderColor: `${gold}30` }}>
                    <DynamicIcon name={item.icon} className="w-4 h-4" fallbackClassName="text-lg leading-none" style={{ color: gold }} />
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-normal text-sm tracking-[0.08em] uppercase text-white mb-3">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed font-normal" style={{ color: 'rgba(250,250,249,0.45)' }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#0a0a0a' }} className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-0 divide-y" style={{ borderColor: `${gold}15` }}>
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-6 py-7">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 border" style={{ borderColor: `${gold}20` }}>
                  <DynamicIcon name={item.icon} className="w-4 h-4" fallbackClassName="text-lg leading-none" style={{ color: gold }} />
                </div>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-normal text-sm tracking-[0.08em] uppercase text-white mb-1.5">{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed font-normal" style={{ color: 'rgba(250,250,249,0.4)' }}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // EDITORIAL — NYT/Medium, serif, rules, asymmetric
  // ═══════════════════════════════════════════

  if (universe === 'editorial') {
    const layout = variant.replace('editorial-', '')

    const header = (
      <div className={cn("mb-16 space-y-4", textAlign ? getTextAlignClass(textAlign) : 'text-center')}>
        {eyebrow && (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-block text-[0.6875rem] font-normal tracking-[0.15em] uppercase text-zinc-900">
            {eyebrow}
          </span>
        )}
        <div className="w-full h-px bg-zinc-900 mx-auto max-w-[120px]" />
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-normal leading-tight tracking-tight", titleSize && getTitleSizeClass(titleSize))} style={{ color: customTextColor ?? '#18181b', fontFamily: 'var(--font-heading, Georgia, serif)' }}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base max-w-xl mx-auto leading-relaxed" style={{ color: '#52525b', fontFamily: 'var(--font-heading, Georgia, serif)' }}>{subtitle}</p>}
      </div>
    )

    if (layout === 'grid' || layout === 'cards') {
      return (
        <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#fafaf9' }} className="py-24">
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((item, i) => (
                <div key={item.id} className="space-y-3 pt-5 border-t border-zinc-900">
                  <span className="text-[0.6875rem] tracking-[0.12em] uppercase text-zinc-400">0{i + 1}</span>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-normal text-base" style={{ color: '#18181b', fontFamily: 'var(--font-heading, Georgia, serif)' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#52525b', fontFamily: 'var(--font-heading, Georgia, serif)' }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#fafaf9' }} className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-0 divide-y divide-zinc-900">
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-6 py-7">
                <span className="text-[0.6875rem] tracking-[0.12em] text-zinc-400 shrink-0 mt-1">0{i + 1}</span>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-normal text-base mb-1" style={{ color: '#18181b', fontFamily: 'var(--font-heading, Georgia, serif)' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#52525b', fontFamily: 'var(--font-heading, Georgia, serif)' }}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // ORGANIC — Nature, rounded, green, earthy
  // ═══════════════════════════════════════════

  if (universe === 'organic') {
    const green = accentColor ?? '#3f6212'
    const layout = variant.replace('organic-', '')

    const header = (
      <div className={cn("text-center mb-14 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {eyebrow ? (
          <span {...elementProps(config.id, 'eyebrow', 'badge')} className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.08em] uppercase px-4 py-1.5 rounded-full" style={{ backgroundColor: `${green}12`, color: green }}>
            <span>🌿</span>
            {eyebrow}
          </span>
        ) : isEditing && <EditablePlaceholder sectionId={config.id} contentPath="eyebrow" type="badge" className="mb-4" />}
        {title && (
          <h2 {...elementProps(config.id, 'title', 'heading')} className={cn("text-3xl md:text-4xl font-normal leading-tight tracking-tight", titleSize && getTitleSizeClass(titleSize))} style={{ color: customTextColor ?? '#1a2e05', fontFamily: 'var(--font-heading, Georgia, serif)' }}>
            {title}
          </h2>
        )}
        {subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base max-w-xl mx-auto leading-relaxed" style={{ color: '#4d7c0f' }}>{subtitle}</p>}
      </div>
    )

    if (layout === 'grid' || layout === 'cards') {
      return (
        <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#f7fee7' }} className="py-24">
          <div className="max-w-6xl mx-auto px-6">
            {header}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item, i) => (
                <div key={item.id} className="rounded-xl p-7 border transition-all hover:shadow-md" style={{ borderColor: `${green}15`, background: '#ffffff' }}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-lg mb-5" style={{ backgroundColor: `${green}10` }}>
                    <DynamicIcon name={item.icon} className="w-5 h-5" fallbackClassName="text-xl leading-none" style={{ color: green }} />
                  </div>
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold mb-2" style={{ color: '#1a2e05' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#4d7c0f' }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )
    }

    // List
    return (
      <section style={{ fontFamily: 'var(--font-body, inherit)', background: '#f7fee7' }} className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-4">
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-5 p-5 rounded-xl border" style={{ borderColor: `${green}12`, background: '#ffffff' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${green}10` }}>
                  <DynamicIcon name={item.icon} className="w-4 h-4" fallbackClassName="text-lg leading-none" style={{ color: green }} />
                </div>
                <div className="flex-1">
                  <h3 {...elementProps(config.id, `items.${i}.title`, 'heading')} className="font-semibold mb-1" style={{ color: '#1a2e05' }}>{item.title}</h3>
                  <p {...elementProps(config.id, `items.${i}.description`, 'text')} className="text-sm leading-relaxed" style={{ color: '#4d7c0f' }}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // fallback → startup-grid
  return <FeaturesSection config={{ ...config, variant: 'startup-grid' }} />
}

export const featuresMeta = {
  type: 'features',
  label: 'Features',
  icon: '✦',
  variants: [
    'startup-grid', 'startup-bento', 'startup-list',
    'corporate-grid', 'corporate-bento', 'corporate-list',
    'luxe-grid', 'luxe-bento', 'luxe-list',
    'creative-grid', 'creative-bento', 'creative-list',
    'ecommerce-grid', 'ecommerce-bento', 'ecommerce-list',
    'glass-grid', 'glass-bento', 'glass-list',
    'canopy-values',
    'nacre-accordion',
    'obscura-accordion',
    'braise-accordion',
    'forge-accordion',
    'ciseaux-accordion',
    'atelier-accordion',
    'encre-accordion',
    'serenite-accordion',
    'pulse-accordion',
    'brixsa-accordion',
    'brixsa-services',
    'brixsa-location',
    'zmr-events',
    'zmr-measurements',
    'zmr-experience',
    'saveur-accordion',
    'ascent-accordion',
    'zenith-accordion',
    'miel-accordion',
    'prisme-accordion',
    'petale-accordion',
    'jlstudio-scroll-pin',
  
    'warm-grid', 'warm-list', 'playful-grid', 'playful-list', 'retro-grid', 'retro-list', 'dark-premium-grid', 'dark-premium-list', 'editorial-grid', 'editorial-list', 'organic-grid', 'organic-list',
  ],
  defaultVariant: 'startup-grid',
  defaultContent: {},
}
